import { inject } from '@angular/core';

import { Router } from '@angular/router';
import { ApolloError } from '@apollo/client/errors';
import { dayjs, UserService } from '@karve.it/core';
import {
    Actions,
    createEffect,
    ofType,
} from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import type { Action } from '@ngrx/store';

import { sortBy } from 'lodash';
import { dispatchStorageKey } from '../utils/dispatch.storage';
import {
    concat,
    EMPTY,
    forkJoin,
    from,
    last,
    merge,
    of,
} from 'rxjs';
import {
    buffer,
    catchError,
    concatMap,
    debounceTime,
    exhaustMap,
    filter,
    groupBy,
    map,
    mergeMap,
    switchMap,
    take,
    tap,
    toArray,
    withLatestFrom,
} from 'rxjs/operators';



import {
    Asset,
    BulkEditCalendarEventGQL,
    BulkEditCalendarEventMutationVariables,
    CalendarEventsFilter,
    CreateCalendarEventGQL,
    CreateCalendarEventMutationVariables,
    EditProfileGQL,
    EditProfileMutationVariables,
    GetConfigValuesGQL,
    ListBaseAssetsGQL,
    ListBaseAssetsQueryVariables,
    ListCalendarEventsGQL,
    ListDispatchUsersGQL,
    ListRolesGQL,
    ListRolesQueryVariables,
    UpdateJobGQL,
    ListUsersQueryVariables,
    LocationsGQL,
    NotifyGQL,
    NotifyMutationVariables,
    OptimizeDateGQL,
    ListAssetScheduleThreadsGQL,
    PreviewCrewScheduleGQL,
    SendScheduleToCrewGQL,
    RemoveCalendarEventGQL,
    RemoveCalendarEventMutationVariables,
    Role,
    ScheduleEventsGQL,
    ScheduleEventsQueryVariables,
    User,
    ZoneDir,
    ZoneConfigKey,
    UpdateAssetsGQL
} from '../../../../generated/graphql.generated';

import { generateMutationContext } from '../../../core/auth/session';
import { BOOK_OFF_EVENT_TYPE, EventAttendeeRoles } from '../../../global.constants';
import { BrandingService } from '../../../services/branding.service';
import { FreyaNotificationsService } from '../../../services/freya-notifications.service';
import { PageTitleService } from '../../../services/page-title.service';
import { PermissionService } from '../../../services/permission.service';


import { TimezoneHelperService } from '../../../services/timezone-helper.service';
import { convertQuillContentsToText, replaceQuillVariablesWithActualValues } from '../../../shared/quill-modules/quill-templates.helpers';
import { brandingFeature } from '../../../state/branding.store';
import { generateCrewCsv } from '../dispatch-csv.helper';



import {
    extractLastSentHashes,
    generateCrewScheduleHash,
} from '../utils/schedule-hash';

import { DispatchActions } from './dispatch.actions';


import {
    AUTO_GENERATED_META_KEY,
    buildDefaultBlockInput,
    DefaultBlockConfig,
    findMissingDefaultBlocks,
    parseDefaultBlocksConfig,
} from './dispatch.default-blocks';


import {
    AdditionalEventStatuses,
    CrewScheduleSendStatus,
    DispatchAllowedToDisplayStatuses,
    NON_BILLABLE_EVENT_TYPE,
    OPS_MANAGER_EVENT_TYPE,
    OPS_TASK_EVENT_TYPE,
    opsTaskFieldsToMetadata,
    opsTaskNotesPlainText,
    RESERVE_EVENT_TYPE,
    toLocalIsoDateString,
    UpdateCrewActionPayload,
    DispatchPendingChange,
} from './dispatch.reducer';
import {
    selectAutoGeneratedBlocks,
    selectCrewRoleIds,
    selectCrewScheduleRecepients,
    selectDefaultBlocksConfig,
    selectDefaultBlocksEnsuredDates,
    selectDispatchDate,
    selectDispatchDateTo,
    selectEventStatus,
    selectEvents,
    selectEventsLoaded,
    selectIsCreatingUser,
    selectIsSavingChanges,
    selectAutosaveEnabled,
    selectPendingChanges,
    selectPotentialCrew,
    selectRoles,
    selectSelectedUserBookOffEvents,
    selectSelectedUserForRateEdit,
    selectSendScheduleAssetNotes,
    selectSendScheduleCrewNotes,
    selectSendScheduleBatches,
    selectSendScheduleRouteTree,
    selectTemplateVariableData,
    selectUsers,
    selectWorkingCrew
} from './dispatch.selectors';

function calculateDateRange(date: Date, dateTo?: Date | null): { min: number; max: number } {
    const startOfDay = dayjs(date).startOf('day').unix();
    // Map's range end (inclusive) widens the window; single-day tabs keep
    // the day's bounds.
    const end = dateTo ? dayjs(dateTo) : dayjs(date);
    const endOfDay = end.endOf('day').unix();
    return { min: startOfDay, max: endOfDay };
}

export const changeUrl = createEffect(
    (actions$ = inject(Actions), store = inject(Store), router = inject(Router), titleService = inject(PageTitleService)) => {
        return actions$.pipe(
            ofType(DispatchActions.setDispatchDate, DispatchActions.hardRefreshEvents),
            concatLatestFrom(() => [store.select(selectDispatchDate), store.select(selectDispatchDateTo)]),
            tap(([_, date, dateTo]) => {
                const dayJs = dayjs(date);
                try {
                    const isMap = router.url.includes('/schedule/map');
                    const viewName = isMap ? 'Map' : 'Dispatch';
                    const pageTitle = `Schedule: ${viewName} - ${dayJs.format('MMM D, YYYY')}`;

                    titleService.setCustomPageTitle({
                        pageTitle,
                    });

                    const basePath = isMap ? '/schedule/map/' : '/schedule/dispatch/';

                    // Targets computed BEFORE the guard so both ends must
                    // agree before we skip: the Router's params can be stale
                    // after the controls service's replaceState (or carry a
                    // stray dateTo from a previous map visit) — a same-date
                    // skip must not leave an un-synced range end behind
                    // (CodeRabbit).
                    const routerParams = router.parseUrl(router.url).queryParams;
                    const currentPath = router.url.split('?')[0];
                    const currentParam = routerParams['date'];
                    const currentTo = routerParams['dateTo'] ?? null;
                    const newParam = dayJs.format('YYYY-MM-DD');
                    // The map's range end rides Angular's navigation: the crumb
                    // picker writes dateTo via the controls service's
                    // replaceState, which the Router cannot see — a merge-only
                    // nav would resurrect the Router's stale (pre-replace)
                    // params and DROP the just-picked range end (live repro:
                    // pick a map range → 300ms later the nav rewrote
                    // ?date=... without dateTo). On the map the store end is
                    // authoritative; elsewhere null removes a stray param.
                    const newDateTo = isMap && dateTo ? dayjs(dateTo).format('YYYY-MM-DD') : null;

                    if (currentPath.includes(basePath) && currentParam === newParam && currentTo === newDateTo) {
                         return;
                    }                    router.navigate([basePath], {
                        queryParamsHandling: 'merge',
                        queryParams: { date: newParam, dateTo: newDateTo },
                    });

                } catch (error) {
                    console.error('Error updating URL:', error);
                }
            }),
        );
    },
    { functional: true, dispatch: false }
);

/**
 * After a route's schedule is successfully sent, refresh its asset threads so
 * the thread history stays current, and immediately mark all sent crew members
 * as 'up-to-date' so the status badges update without a full page reload.
 *
 * Dispatches multiple actions: one sendScheduleAssetThreadsLoaded + one
 * sendScheduleCrewStatusesLoaded per success event.
 */
// eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
export const RefreshStatusAfterSend = createEffect(
    (
        actions$ = inject(Actions),
        listThreadsGQL = inject(ListAssetScheduleThreadsGQL),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.sendScheduleSubmitSuccess),
            mergeMap(({ assetId, sentUserIds }) => {
                const refreshThreads$ = listThreadsGQL.fetch(
                    { objectIds: [assetId], threadMode: ['Send Schedule to Crew'], limit: 10 },
                    { fetchPolicy: 'network-only' },
                ).pipe(
                    map((res) => {
                        const threads = res.data?.comments?.comments || [];
                        return DispatchActions.sendScheduleAssetThreadsLoaded({ assetId, threads });
                    }),
                    catchError(() => of(DispatchActions.sendScheduleAssetThreadsLoaded({ assetId, threads: [] }))),
                );

                const updateStatuses$ = of(DispatchActions.sendScheduleCrewStatusesLoaded({
                    statuses: sentUserIds.map((userId) => ({
                        userId,
                        assetId,
                        status: 'up-to-date' as const,
                    })),
                }));

                return merge(refreshThreads$, updateStatuses$);
            }),
        );
    },
    { functional: true, dispatch: true }
);

export const getEvents = createEffect(
    (
        actions$ = inject(Actions),
        scheduleEventsGQL = inject(ScheduleEventsGQL),
        store = inject(Store),
        router = inject(Router),
    ) => {
        return actions$.pipe(
            ofType(
                DispatchActions.setDispatchDate,
                DispatchActions.setEventStatus,
                DispatchActions.hardRefreshEvents,
                DispatchActions.componentHardRefreshed,
                DispatchActions.crewMembersAssignedWithAISuccessfully,
            ),
            withLatestFrom(
                store.select(selectDispatchDate),
                store.select(selectEventStatus),
                store.select(selectDispatchDateTo),
            ),
            exhaustMap(([action, date, status, _dateTo]) => {
                store.dispatch(DispatchActions.eventsLoading());

                // Only the map tab exposes a range; dispatch stays single-day
                // even if a stale range end lingers in the store.
                const isMapTab = router.url.includes('/schedule/map');
                const { min, max } = calculateDateRange(date, isMapTab ? _dateTo : null);
                const variables: ScheduleEventsQueryVariables = {
                    filter: {
                        min,
                        max,
                        getEventsOfStatus: Object.values(DispatchAllowedToDisplayStatuses),
                        dontIncludeBookOff: true,
                    },
                    limit: 100,
                    resolve: ['charges', 'assets', 'job'],
                };

                if (status != AdditionalEventStatuses.All) {
                    variables.filter.status = status;
                }

                const hardRefreshActions = [
                    DispatchActions.hardRefreshEvents.type,
                    DispatchActions.componentHardRefreshed.type,
                    DispatchActions.crewMembersAssignedWithAISuccessfully.type,
                ] as const;

                const fetchPolicy = hardRefreshActions.includes(action.type as any)
                    ? 'network-only' : 'cache-first';

                return scheduleEventsGQL.fetch(variables, { fetchPolicy }).pipe(
                    // Read pending changes when the RESPONSE arrives, not when
                    // the fetch starts — notes typed while the request was in
                    // flight must be detected, otherwise eventsLoaded replaces
                    // the events and the typed note is never re-applied.
                    concatLatestFrom(() => store.select(selectPendingChanges)),
                    map(([res, pendingChanges]) => {
                        if (res.loading || !res?.data) {
                            return DispatchActions.eventsLoading();
                        }

                        const { calendarEvents } = res.data;

                        const filteredEvents = calendarEvents.events.filter(e => e.type !== 'estimating' && e.type !== 'virtualEstimate');

                        const hasPendingCrewNotes = pendingChanges.some(
                            (c) => c.type === 'editEvent' && c.eventData?.eventNotes !== undefined,
                        );
                        if (hasPendingCrewNotes) {
                            return DispatchActions.replayPendingCrewNotes({
                                changes: pendingChanges,
                                events: filteredEvents,
                            });
                        }

                        return DispatchActions.eventsLoaded({
                            events: filteredEvents,
                        });
                    }),

                    catchError((error) => {
                        return of(DispatchActions.eventsLoadError({ error }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true },
);

export const uploadCrewListAsCsv = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        userService = inject(UserService),
        editProfileGQL = inject(EditProfileGQL),
        notify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.uploadCrewListCsv),
            withLatestFrom(
                store.select(selectUsers),
                store.select(selectRoles)
            ),
            exhaustMap(([{ csvData }, users, roles]) => {
                // const crewByEmail = new Map(crew.map(c => [c.user.email, c]));

                const crewRoles = sortBy(
                    roles.filter(r => r.name.includes('Crew Member')),
                    (r) => -r.zones.nodes[0].zoneDepth
                );

                const crewMemberRole = crewRoles[0];

                if (!crewMemberRole) {
                    notify.error('"Crew Member" role not found. Please ensure it exists before uploading a CSV.');
                    return EMPTY;
                }

                const rows = csvData.split('\n').slice(1);

                const usersToCreate = [];
                const usersToUpdate = [];

                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                for (const row of rows) {
                    if (!row) continue;

                    const column = row.split(',').map(value => {
                        const columnValue = value.trim().replace(/"/g, '');
                        return columnValue === '' ? undefined : columnValue;
                    });

                    const [
                        givenName, familyName, rawEmail, phone, rate, capabilities, available,
                        sun, mon, tue, wed, thu, fri, sat,
                        timezone, maxHours, maxHoursPerWeek, payPeriodStart, overtimeRate
                    ] = column;

                    // Check if email is valid
                    const isValidEmail = emailRegex.test(rawEmail);
                    const email = isValidEmail ? rawEmail : null;

                    const ignorePhones = [
                        `10000000000`, // dev phone
                        `1111111111`,
                        `11111111111`,
                        `0000000000`,
                        `00000000000`,
                    ];

                    const existingUsers = users.filter(u => {
                        const emailMatch = isValidEmail && u.email === email;
                        const phoneMatch = phone && u.phone === phone && !ignorePhones.includes(phone);
                        const nameMatch = givenName && familyName && u.givenName === givenName && u.familyName === familyName;

                        return emailMatch || phoneMatch || nameMatch;
                    });

                    const existingUser = existingUsers[0];
                    if (existingUsers.length > 1) {
                        notify.warning(`Potential duplicate user`, existingUser.email || existingUser.phone || existingUser.familyName);

                    }

                    const availability = [sun, mon, tue, wed, thu, fri, sat].map((a, i) => {
                        if (a) return `Y${a}`;
                        if (a === undefined) return existingUser?.availability?.[i] ?? 'N';
                        return 'N';
                    });

                    const user: Partial<User> = {
                        givenName,
                        familyName,
                        phone,
                        rateHourly: parseFloat(rate) * 100,
                        capabilities: capabilities ? capabilities.split(';') : undefined,
                        available: (available?.toLowerCase()) === 'true',
                        availability,
                        timezone,
                        maxHoursPerDay: parseInt(maxHours),
                        maxHoursPerWeek: parseFloat(maxHoursPerWeek),
                        payPeriodStart: parseInt(payPeriodStart),
                        overtimeRate: parseFloat(overtimeRate),
                    };

                    if (isValidEmail) {
                        user.email = email;
                    }

                    if (existingUser) {
                        const changedProfile: EditProfileMutationVariables = { id: existingUser.id };
                        let hasChanges = false;

                        const rateHourly = Math.round(parseFloat(rate) * 100);
                        const maxHoursInt = parseInt(maxHours, 10);
                        const maxHoursWeekFloat = parseFloat(maxHoursPerWeek);
                        const payPeriodStartInt = parseInt(payPeriodStart, 10);
                        const availableBool = available?.toLowerCase() === 'true';

                        if (givenName && givenName !== existingUser.givenName) {
                            changedProfile.givenName = givenName;
                            hasChanges = true;
                        }
                        if (familyName && familyName !== existingUser.familyName) {
                            changedProfile.familyName = familyName;
                            hasChanges = true;
                        }
                        // Only update email if we have a valid one from CSV
                        // and it's different from existing.
                        // However, requirement says "throw it away" if invalid,
                        // and logic usually implies updating fields if provided.
                        // If CSV has valid email and it differs, we update.
                        if (isValidEmail && email.toLowerCase() !== existingUser.email?.toLowerCase()) {
                            changedProfile.email = email;
                            hasChanges = true;
                        }
                        if (phone && phone !== existingUser.phone) {
                            changedProfile.phone = phone;
                            hasChanges = true;
                        }
                        if (!isNaN(rateHourly) && rateHourly !== existingUser.rateHourly) {
                            changedProfile.rateHourly = rateHourly;
                            hasChanges = true;
                        }
                        if (available && availableBool !== existingUser.available) {
                            changedProfile.available = availableBool;
                            hasChanges = true;
                        }
                        if (JSON.stringify(availability) !== JSON.stringify(existingUser.availability)) {
                            changedProfile.availability = availability;
                            hasChanges = true;
                        }
                        if (timezone && timezone !== existingUser.timezone) {
                            changedProfile.timezone = timezone;
                            hasChanges = true;
                        }
                        if (!isNaN(maxHoursInt) && maxHoursInt !== existingUser.maxHoursPerDay) {
                            changedProfile.maxHoursPerDay = maxHoursInt;
                            hasChanges = true;
                        }
                        if (!isNaN(maxHoursWeekFloat) && maxHoursWeekFloat !== existingUser.maxHoursPerWeek) {
                            changedProfile.maxHoursPerWeek = maxHoursWeekFloat;
                            hasChanges = true;
                        }
                        if (!isNaN(payPeriodStartInt) && payPeriodStartInt !== existingUser.payPeriodStart) {
                            changedProfile.payPeriodStart = payPeriodStartInt;
                            hasChanges = true;
                        }
                        if (capabilities && JSON.stringify(capabilities.split(';')) !== JSON.stringify(existingUser.capabilities)) {
                            changedProfile.capabilities = capabilities.split(';');
                            hasChanges = true;
                        }

                        if (hasChanges) {
                            usersToUpdate.push(changedProfile);
                        }
                    } else {
                        usersToCreate.push(user);
                    }
                }

                store.dispatch(DispatchActions.usersLoading());
                const create$ = usersToCreate.length ? userService.createUsers({
                    users: usersToCreate,
                    roles: [crewMemberRole.id],
                    inheritRoleZones: true,
                }) : of(null);

                const update$ = from(usersToUpdate).pipe(
                    mergeMap(user => {
                        const { id, ...profile } = user;
                        return editProfileGQL.mutate({ id, ...profile });
                    }),
                    toArray()
                );

                return forkJoin([create$, update$]).pipe(
                    map(([createResult, updateResult]) => {
                        const createdCount = createResult ? createResult.total : 0;
                        const failedToCreate = createResult ? createResult.failedUsers : [];
                        const updatedCount = updateResult ? updateResult.length : 0;
                        // This error handling is necessary because userService.createUsers
                        // does not throw an error if it fails to be created.
                        // It return an object with failedUsers array.
                        if(failedToCreate.length > 0) {
                            failedToCreate.map((failedCreatedUser) => {
                                notify.error(`Failed to create user: ${failedCreatedUser.user.email}.`, failedCreatedUser.error);});
                        }
                        notify.success(`Successfully created ${createdCount} and updated ${updatedCount} users.`);
                        return DispatchActions.crewListUpdated();
                    }),
                    catchError((error) => {
                        notify.apolloError('Failed to upload crew list.', error);
                        return EMPTY;
                    })
                );
            })
        );
    },
    { functional: true }
);

export const downloadCrewListAsCsv = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        brandingService = inject(BrandingService),
        tzHelper = inject(TimezoneHelperService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.downloadCrewListAsCsv),
            withLatestFrom(
                store.select(selectPotentialCrew),
                brandingService.currentZone(),
                store.select(selectDispatchDate)
            ),
            map(([{ crew }, storeCrew, zone, date]) => {
                const crewList = crew || storeCrew;
                const currentTimezone = tzHelper.getCurrentTimezone();
                const { blob, filename } = generateCrewCsv(crewList, zone.name, date, currentTimezone);

                const link = document.createElement("a");
                const url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute("download", filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
        );
    },
    { functional: true, dispatch: false },
);


export const GetRoles = createEffect(
    (actions$ = inject(Actions), listRolesGQL = inject(ListRolesGQL), permissionHandler = inject(PermissionService)) => {
        return actions$.pipe(
            ofType(DispatchActions.initCrew, DispatchActions.componentHardRefreshed, DispatchActions.crewListUpdated),
            // Pair the trigger with the first permission emission; concatLatestFrom
            // drops the action when watchPermission hasn't emitted yet, which
            // would leave roles unloaded until the next trigger.
            concatMap((action) => permissionHandler.watchPermission('roles.view').pipe(
                take(1),
                map((hasRolesView) => [ action, hasRolesView ] as const),
            )),
            filter(([, hasRolesView]) => hasRolesView),
            // `switchMap`, not `exhaustMap`: componentHardRefreshed arriving while
            // a fetch is active must supersede it, or the network-only refresh
            // never runs and the store keeps stale roles.
            switchMap(([action]) => {
                const variables: ListRolesQueryVariables = {
                    search: {
                        zoneDir: ZoneDir.Any,
                        limit: -1,
                    },
                };

                const fetchPolicy = action.type === DispatchActions.componentHardRefreshed.type ? 'network-only' : 'cache-first';

                return listRolesGQL.fetch(variables, { fetchPolicy }).pipe(
                    map((res) => {
                        if (res.loading || !res?.data) {
                            return DispatchActions.rolesLoading();
                        }

                        const { roles } = res.data;

                        const actionType = action.type === DispatchActions.componentHardRefreshed.type
                            ? DispatchActions.rolesLoadedOnComponentHardRefresh
                            : DispatchActions.rolesLoaded;

                        return actionType({ roles: roles as Role[] });
                    }),

                    catchError((error) => {
                        return of(DispatchActions.rolesLoadError({ error }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true },
);

export const GetAssets = createEffect(
    (actions$ = inject(Actions), listAssets = inject(ListBaseAssetsGQL), permissionHandler = inject(PermissionService)) => {
        return actions$.pipe(
            ofType(DispatchActions.initCrew, DispatchActions.componentHardRefreshed),
            // Pair the trigger with the first permission emission; concatLatestFrom
            // drops the action when watchPermission hasn't emitted yet, which
            // would leave assets unloaded until the next trigger.
            concatMap((action) => permissionHandler.watchPermission('assets.view').pipe(
                take(1),
                map((hasAssetsView) => [ action, hasAssetsView ] as const),
            )),
            filter(([, hasAssetsView]) => hasAssetsView),
            // `switchMap`, not `exhaustMap`: componentHardRefreshed arriving while
            // a fetch is active must supersede it, or the network-only refresh
            // never runs and the store keeps stale assets.
            switchMap(([action]) => {
                const variables: ListBaseAssetsQueryVariables = {
                    filter: {
                        types: ['Truck'],
                    },
                    limit: -1,
                };

                const fetchPolicy = action.type === DispatchActions.componentHardRefreshed.type ? 'network-only' : 'cache-first';

                return listAssets.fetch(variables, { fetchPolicy }).pipe(
                    map((res) => {
                        if (res.loading || !res?.data) {
                            return DispatchActions.assetsLoading();
                        }

                        const { assets } = res.data;

                        return DispatchActions.assetsLoaded({
                            assets: assets.assets as Asset[],
                        });
                    }),

                    catchError((error) => {
                        return of(DispatchActions.assetsLoadError({ error }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true },
);
export const GetUsers = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        listUsersGQL = inject(ListDispatchUsersGQL),
    ) => {
        return actions$.pipe(
            ofType(
                DispatchActions.setUserRole,
                DispatchActions.rolesLoaded,
                DispatchActions.rolesLoadedOnComponentHardRefresh,
                DispatchActions.updateCrewError,
                DispatchActions.crewListUpdated,
                DispatchActions.createCrewMemberSuccess,
            ),
            withLatestFrom(
                store.select(selectCrewRoleIds),
            ),
            exhaustMap(([action, crewRoleIds]) => {
                if (crewRoleIds.length === 0) {
                    return EMPTY;
                }

                store.dispatch(DispatchActions.usersLoading());

                const variables: ListUsersQueryVariables = {
                    filter: {
                        // search, // We do local search instead
                        roles: crewRoleIds,
                    },
                    limit: -1,
                };

                const hardReload = [
                    DispatchActions.rolesLoadedOnComponentHardRefresh.type,
                    DispatchActions.updateCrewError.type,
                    DispatchActions.crewListUpdated.type,
                ].includes(action.type as any);

                const fetchPolicy = hardReload ? 'network-only' : 'cache-first';

                return listUsersGQL.fetch(variables, { fetchPolicy }).pipe(
                    map((res) => {
                        if (res.loading || !res?.data) {
                            return DispatchActions.usersLoading();
                        }

                        const users  = res?.data?.usersv2?.users ?? [];

                        return DispatchActions.usersLoaded({
                            users: users as User[],
                        });
                    }),

                    catchError((error) => {
                        return of(DispatchActions.usersLoadError({ error }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true },
);

// UpdateEventCrew now only handles the optimistic state update.
// The actual bulkEdit call happens in SavePendingChanges when the user clicks Save.
export const UpdateEventCrew = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DispatchActions.updateCrew),
            map(({ edits }) => DispatchActions.updateCrewSuccess({ edits })),
        );
    },
    { functional: true, dispatch: true },
);

export const NotifyCrewSettingsUpdated = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        localNotify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.crewSettingsSavedSuccessfully),
            withLatestFrom(store.select(selectSelectedUserForRateEdit)),
            tap(([action, selectedUser]) => {
                const updatedUser = selectedUser?.id === action.id ? selectedUser : null;

                if (!updatedUser) {
                    console.error('Failed to get updated crew', action.id);
                    return;
                }

                const { givenName, familyName } = updatedUser;

                localNotify.addToast.next({
                    severity: 'success',
                    summary: `${familyName}, ${givenName} -- Changes saved`,
                });
            }),
            // Reload the crew list so capability changes (e.g. adding Operations)
            // are reflected in the ops manager pool and crew sidebar.
            map(() => DispatchActions.crewListUpdated()),
        );
    },
    { functional: true, dispatch: true },
);


export const UpdateCrewSettings = createEffect(
    (
        actions$ = inject(Actions),
        editProfileGQL = inject(EditProfileGQL),
        notify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.crewSettingsUpdated),
            groupBy((action) => action.id),
            mergeMap((group$) => group$.pipe(
                buffer(group$.pipe(debounceTime(1000))),
                map((actions) => actions.reduce(
                    (acc, curr) => ({ ...acc, ...curr }), {}) as typeof actions[0]
                ),
                exhaustMap((action) => {
                    const input: EditProfileMutationVariables = {
                        ...action,
                    };

                    return editProfileGQL.mutate(input).pipe(
                        map((res) => {
                            const success = res.data?.users?.setProfile;

                            if (!success) {
                                console.error('Failed to update crew', res.errors);
                                return DispatchActions.errorSavingCrewSettings({ error: new Error(res.errors[0].message) });
                            }
                            return DispatchActions.crewSettingsSavedSuccessfully(
                                input
                            );
                        }),
                        catchError((error) => {
                            console.error('Failed to update crew', error);
                            if (error instanceof ApolloError) {
                                notify.apolloError('Failed to update crew.', error);                          
                            } else {
                                notify.error('Failed to update crew.', error);
                            }
                            return of(DispatchActions.errorSavingCrewSettings({ error }));
                        }),
                    );
                }),
            )),
        );
    },
    { functional: true, dispatch: true },
);

export const CrewBookOffCreated = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        createCalendarEventGQL = inject(CreateCalendarEventGQL),
        notify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.crewBookOffCreated),
            withLatestFrom(
                store.select(selectSelectedUserForRateEdit),
            ),
            exhaustMap(([{ newBookOff }, selectedUser]) => {
                const userId = newBookOff.userId || selectedUser?.id;

                if (!userId || newBookOff.start === null || newBookOff.end === null) {
                    const error = new Error('Invalid book-off input.');
                    notify.error('Unable to create book-off. Missing date.');
                    return of(DispatchActions.errorSavingCrewSettings({ error }));
                }

                const variables: CreateCalendarEventMutationVariables = {
                    calendarEvents: [{
                        title: 'Booked Off',
                        description: newBookOff.description ?? '',
                        start: newBookOff.start,
                        end: newBookOff.end,
                        type: BOOK_OFF_EVENT_TYPE,
                        status: 'confirmed',
                        attendees: [{ userId: userId, role: 'Attendee' }],
                        attributes: ['disable-notifications']
                    }]
                };

                return createCalendarEventGQL.mutate(variables).pipe(
                    map((res) => {
                        const [createdEvent] = res.data?.createCalendarEvent?.events ?? [];

                        if (!createdEvent) {
                            throw new Error(`Failed to create book-off - it was not returned`);
                        }

                        notify.success('Book-off scheduled for crew member.');
                        return DispatchActions.crewBookOffEventCreatedSuccess({
                            calendarEvent: createdEvent,
                        });
                    }),
                    catchError((error) => {
                        console.error('Failed to create book-off.', error);
                        notify.error('Failed to create book-off.');
                        return of(DispatchActions.crewBookOffEventCreatedError({ error }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true },
);



export const DeleteCrewBookOffEvent = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        removeCalendarEventGQL = inject(RemoveCalendarEventGQL),
        localNotify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.deleteCrewBookOffEvent),
            withLatestFrom(
                store.select(selectSelectedUserForRateEdit),
                store.select(selectSelectedUserBookOffEvents)
            ),
            exhaustMap(([{ bookOffId }, {
                id: _id,
            }]) => {
                const input: RemoveCalendarEventMutationVariables = {
                    ids: [bookOffId],
                };

                return removeCalendarEventGQL.mutate(input).pipe(
                    map((res) => {
                        const success = res.data.removeCalendarEvent;

                        if (!success) {
                            throw new Error(`Failed to delete book off ${ bookOffId }`);
                        }
                        localNotify.addToast.next({
                            severity: 'success',
                            summary: 'Book-Off deleted',
                        });
                        return DispatchActions.crewBookOffEventDeletedSuccess({
                            bookOffId,
                        });
                    }),
                    catchError((error) => {
                        console.error('Failed to delete book-off.', error);
                        localNotify.addToast.next({
                            severity: 'error',
                            summary: 'Failed to deleted book off',
                            detail: error.message,
                        });
                        return of(DispatchActions.crewBookOffDeletedError({ error }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true },
);

export const GetCrewBookOffEvents = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        crewBookOffEvents = inject(ListCalendarEventsGQL),
    ) => {
        return actions$.pipe(
            ofType(
                DispatchActions.editCrewSettingsUserSelected,
                DispatchActions.crewBookOffEventCreatedSuccess,
                DispatchActions.crewBookOffEventDeletedSuccess,
            ),
            withLatestFrom(
                store.select(selectSelectedUserForRateEdit),
                store.select(selectSelectedUserBookOffEvents),
                store.select(selectDispatchDate),
            ),
            filter(([_action, _user, _events]) => {
                return Boolean(_user);
            }),
            exhaustMap(([_action, {
                id,
            }, _events, dispatchDate]) => {
                const dispatchMorning = dayjs(dispatchDate).startOf('day').unix();
                const filter: CalendarEventsFilter = {
                    // users: [id],
                    type: BOOK_OFF_EVENT_TYPE,
                    hasAttendees: [id],
                    min: dispatchMorning,
                };

                return crewBookOffEvents.fetch({
                    filter: {
                        ...filter
                    },
                },
                    {
                        fetchPolicy: 'network-only'
                    }).pipe(
                        map((res) => {
                            const success = res.data.calendarEvents.events;

                            if (!success) {
                                console.error('Failed to update crew capabilities.', res.errors);
                                return DispatchActions.errorSavingCrewSettings({ error: new Error(res.errors[0].message) });
                            }
                            return DispatchActions.updateCrewBookOffEvents({ bookOffEvents: success });
                        }),
                        catchError((error) => {
                            console.error('Failed to update crew capabilities.', error);
                            return of(DispatchActions.errorSavingCrewSettings({ error }));
                        }),
                    );
            }),
        );
    },
    { functional: true, dispatch: true },
);

// ── Non-Billable Events ─────────────────────────────────────────────────────
// Creates "non-billable" calendar events. Pushed to the pending changes queue
// instead of saving immediately — saved when user clicks Save.
export const CreateNonBillableEvent = createEffect(
    (
        actions$ = inject(Actions),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.nonBillableEventCreated),
            map(({ input }) => {
                return DispatchActions.pendingChangeAdded({
                    change: {
                        id: `create-${input.eventId}`,
                        type: 'createEvent',
                        eventId: input.eventId,
                        eventName: input.description || `Non-Billable (${input.reason})`,
                        eventData: { ...input, id: input.eventId, eventType: NON_BILLABLE_EVENT_TYPE },
                    },
                });
            }),
        );
    },
    { functional: true, dispatch: true },
);

// ── Reserve Events ─────────────────────────────────────────────────────────
// Creates "reserve" calendar events. Pushed to the pending changes queue.
export const CreateReserveEvent = createEffect(
    (
        actions$ = inject(Actions),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.reserveEventCreated),
            map(({ input }) => {
                return DispatchActions.pendingChangeAdded({
                    change: {
                        id: `create-${input.eventId}`,
                        type: 'createEvent',
                        eventId: input.eventId,
                        eventName: input.description || 'Reserve',
                        eventData: { ...input, id: input.eventId, eventType: RESERVE_EVENT_TYPE },
                    },
                });
            }),
        );
    },
    { functional: true, dispatch: true },
);

// ── Ops Manager Events ─────────────────────────────────────────────────────
// Creates "ops-manager" calendar events. Pushed to the pending changes queue.
export const CreateOpsManagerEvent = createEffect(
    (
        actions$ = inject(Actions),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.opsManagerEventCreated),
            map(({ input }) => {
                return DispatchActions.pendingChangeAdded({
                    change: {
                        id: `create-${input.eventId}`,
                        type: 'createEvent',
                        eventId: input.eventId,
                        eventName: input.description || 'Ops Manager',
                        eventData: { ...input, id: input.eventId, eventType: OPS_MANAGER_EVENT_TYPE },
                    },
                });
            }),
        );
    },
    { functional: true, dispatch: true },
);

// ── OPS Tasks ───────────────────────────────────────────────────────────────
// Creates "ops-task" calendar events (Ray's OPS section). Pushed to the
// pending changes queue like every other block. Task fields (client info,
// supplies, storage, confirmation) ride in eventData and are serialized into
// event metadata by SavePendingChanges.
export const CreateOpsTask = createEffect(
    (
        actions$ = inject(Actions),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.opsTaskCreated),
            map(({ input }) => {
                return DispatchActions.pendingChangeAdded({
                    change: {
                        id: `create-${input.eventId}`,
                        type: 'createEvent',
                        eventId: input.eventId,
                        eventName: input.title,
                        eventData: {
                            ...input,
                            id: input.eventId,
                            eventType: OPS_TASK_EVENT_TYPE,
                            // Card fallback text: plain text of the notes delta.
                            description: input.notes ? opsTaskNotesPlainText(input.notes) : input.title,
                        },
                    },
                });
            }),
        );
    },
    { functional: true, dispatch: true },
);

/**
 * OPS task edits (all fields) go through the pending-changes queue as
 * editEvent changes carrying eventData.opsTaskFields. SavePendingChanges
 * turns them into a bulkEdit metadata update. The reducer applies the
 * optimistic event-metadata update (see pendingChangeAdded).
 */
export const EditOpsTask = createEffect(
    (
        actions$ = inject(Actions),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.opsTaskEdited),
            map(({ eventId, input, eventName }) => {
                return DispatchActions.pendingChangeAdded({
                    change: {
                        id: `opsTask-${eventId}-${Date.now()}`,
                        type: 'editEvent',
                        eventId,
                        eventName,
                        newTitle: input.title ?? eventName,
                        eventData: { opsTaskFields: input },
                    },
                });
            }),
        );
    },
    { functional: true, dispatch: true },
);

// On error, hard refresh to get the true state from the server.
// On success, the optimistic update in the reducer is sufficient — no reload needed.
export const RefreshAfterPlaceholderEventError = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                DispatchActions.reserveEventCreateError,
                DispatchActions.nonBillableEventCreateError,
                DispatchActions.opsManagerEventCreateError,
                DispatchActions.opsTaskEditError,
            ),
            map(() => DispatchActions.hardRefreshEvents({ date: null })),
        );
    },
    { functional: true, dispatch: true },
);

// ── Save pending changes ────────────────────────────────────────────────────
// Reads the pending changes queue and sends a single bulkEditCalendarEvent
// mutation with all changes. On success, clears the queue. On error, keeps
// the queue so the user can retry.
// Debounced autosave: 5s after the last pending change is added,
// trigger a save if there are pending changes and no save is in flight.
export const AutosavePendingChanges = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) => {
        return actions$.pipe(
            ofType(
                DispatchActions.pendingChangeAdded,
                DispatchActions.pendingChangeAndOptimisticUpdate,
                DispatchActions.blockDeleted,
            ),
            debounceTime(5000),
            withLatestFrom(
                store.select(selectPendingChanges),
                store.select(selectAutosaveEnabled),
            ),
            filter(([, changes, autosave]) => changes.length > 0 && autosave),
            map(() => DispatchActions.saveChangesClicked()),
        );
    },
    { functional: true, dispatch: true },
);

export const SavePendingChanges = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        bulkEditCalendarEventGQL = inject(BulkEditCalendarEventGQL),
        createCalendarEventGQL = inject(CreateCalendarEventGQL),
        removeCalendarEventGQL = inject(RemoveCalendarEventGQL),
        updateJobGQL = inject(UpdateJobGQL),
        notify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.saveChangesClicked),
            concatLatestFrom(() => [
                store.select(selectPendingChanges),
                store.select(selectEvents),
            ]),
            filter(([, changes]) => changes.length > 0),
            exhaustMap(([, changes, allEvents]) => {
                // Map event ID → existing metadata for merging
                const eventMetadataMap = new Map<string, any>();
                for (const e of allEvents) {
                    if (e.metadata) eventMetadataMap.set(e.id, e.metadata as any);
                }
                // Separate createEvent changes from bulk edit changes
                const createChanges = changes.filter((c) => c.type === 'createEvent');
                const deleteChanges = changes.filter((c) => c.type === 'deleteEvent');

                // Clone createChanges to avoid mutating NgRx-owned state
                const clonedCreates = createChanges.map((c) => ({
                    ...c,
                    eventData: { ...c.eventData, attendees: [...(c.eventData.attendees || [])] },
                }));

                // Build create mutations for createEvent changes
                // Merge removeCrew changes into createEvent changes for events
                // that haven't been created on the server yet. Instead of trying
                // to bulk-edit a non-existent event, update the create input's
                // attendee list directly.
                for (const change of changes) {
                    if (change.type !== 'removeCrew') continue;
                    // CR 4166f5ea: the whole-change validation (removeMalformed)
                    // must gate this merge too. A malformed fromEvent id queues
                    // the change as failed further down, so mutating the pending
                    // create here would half-apply a failed change: the create
                    // submits WITHOUT the crew member while the removeCrew stays
                    // queued as failed. Skipped here, the corrupt change is
                    // marked failed (removeMalformed) and the create keeps its
                    // full attendee list.
                    if ((change.fromEvents || []).some(
                        (entry) => typeof entry?.eventId !== 'string' || entry.eventId === '',
                    )) continue;
                    const targetCreate = clonedCreates.find((c) =>
                        c.eventId === change.eventId
                        || (change.fromEvents || []).some((f) => f.eventId === c.eventId),
                    );
                    if (!targetCreate) continue;
                    const ed = targetCreate.eventData;
                    if (!ed.attendees.length && ed.userId) {
                        ed.attendees = [{ userId: ed.userId, role: EventAttendeeRoles.crewMember }];
                    }
                    ed.attendees = ed.attendees.filter(
                        (a: any) => a.userId !== change.userId,
                    );
                }

                // Merge editEvent changes (title/time) into createEvent changes
                // for events that haven't been created on the server yet.
                // Track which edits ride INSIDE which create — if that create
                // fails, the edits must be retained as failed too, or a retry
                // re-creates the event without them (CodeRabbit).
                const editsMergedIntoCreate = new Map<string, string[]>();
                for (const change of changes) {
                    if (change.type !== 'editEvent') continue;
                    if (change.eventData?.opsNotes !== undefined) continue;
                    const targetCreate = clonedCreates.find((c) => c.eventId === change.eventId);
                    if (!targetCreate) continue;
                    const mergedIds = editsMergedIntoCreate.get(change.eventId) ?? [];
                    mergedIds.push(change.id);
                    editsMergedIntoCreate.set(change.eventId, mergedIds);
                    if (change.newTitle) {
                        targetCreate.eventName = change.newTitle;
                        targetCreate.eventData.description = change.newTitle;
                    }
                    if (change.newStart) targetCreate.eventData.start = change.newStart;
                    if (change.newEnd) targetCreate.eventData.end = change.newEnd;
                    // OPS task field edits merge into the pending create's eventData
                    if (change.eventData?.opsTaskFields && targetCreate.eventData.eventType === OPS_TASK_EVENT_TYPE) {
                        targetCreate.eventName = change.eventData.opsTaskFields.title ?? targetCreate.eventName;
                        targetCreate.eventData.title = targetCreate.eventName;
                        targetCreate.eventData.description = change.eventData.opsTaskFields.notes
                            ?? targetCreate.eventData.description;
                        Object.assign(targetCreate.eventData, change.eventData.opsTaskFields);
                    }
                    // Day notes typed into the card while the event is still a
                    // pending create must ride INTO the create input — otherwise
                    // the edit is silently dropped (it can't bulk-edit an event
                    // that doesn't exist yet) but is still counted as saved.
                    if (change.eventData?.dayNotes !== undefined) {
                        targetCreate.eventData.dayNotes = change.eventData.dayNotes;
                    }
                    // Same for event notes typed into the card mid-create.
                    if (change.eventData?.eventNotes !== undefined) {
                        targetCreate.eventData.eventNotes = change.eventData.eventNotes;
                    }
                    // And for attachments attached to the event mid-create:
                    // without this the edit is counted as saved but dropped.
                    // Raw array here; createOps JSON-stringifies once.
                    if (change.eventData?.eventAttachments !== undefined) {
                        targetCreate.eventData.eventAttachments = change.eventData.eventAttachments;
                    }
                }

                const createOps: any[] = clonedCreates.map((c) => {
                    const ed = c.eventData;
                    const type = ed.eventType || NON_BILLABLE_EVENT_TYPE;
                    // Use attendees from eventData (may have been modified by
                    // addCrew/removeCrew changes merged into the createEvent)
                    const attendees = ed.attendees && ed.attendees.length > 0
                        ? ed.attendees
                        : ed.userId
                            ? [{ userId: ed.userId, role: EventAttendeeRoles.crewMember }]
                            : [];
                    // Metadata: crew requirement (custom block dialog) and the
                    // autoGenerated flag (default blocks config). Both are
                    // JSON-stringified per the Neo4j primitive-value rule.
                    const createMetadata: Record<string, string> = {};
                    if (ed.crewRequirement) {
                        createMetadata.crewRequirement = JSON.stringify(ed.crewRequirement);
                    }
                    if (ed.metadata?.[AUTO_GENERATED_META_KEY]) {
                        createMetadata[AUTO_GENERATED_META_KEY] = String(ed.metadata[AUTO_GENERATED_META_KEY]);
                    }
                    // OPS task fields (client, supplies, storage, confirmation…)
                    if (type === OPS_TASK_EVENT_TYPE) {
                        Object.assign(createMetadata, opsTaskFieldsToMetadata(ed));
                    }
                    // Day notes merged from a mid-save edit (ops-task card editor)
                    if (ed.dayNotes !== undefined) {
                        createMetadata.dayNotes = ed.dayNotes;
                    }
                    // Event notes merged from a mid-save edit (dispatch card editor)
                    if (ed.eventNotes !== undefined) {
                        createMetadata.eventNotes = ed.eventNotes;
                    }
                    // Event attachments merged from a mid-save edit (📎 chips)
                    if (ed.eventAttachments !== undefined) {
                        createMetadata.eventAttachments = JSON.stringify(ed.eventAttachments);
                    }
                    return {
                        title: c.eventName,
                        description: ed.description || '',
                        start: ed.start,
                        end: ed.end,
                        type,
                        // OPS tasks default to 'booked' (Ray) so the completed
                        // toggle reads booked → completed → booked.
                        status: type === OPS_TASK_EVENT_TYPE ? 'booked' : 'confirmed',
                        attendees,
                        attributes: ['disable-notifications'],
                        id: c.eventId,
                        ...(Object.keys(createMetadata).length > 0 ? {
                            metadata: JSON.stringify(createMetadata),
                        } : {}),
                    };
                });

                // Build bulk edit inputs from crew changes
                const editsByEvent = new Map<string, any>();
                // Map event ID → change IDs that contributed to its bulk edit
                const changeIdsByEvent = new Map<string, string[]>();

                // Set of event IDs that are being created (not yet on server)
                const creatingEventIds = new Set(createChanges.map((c) => c.eventId));
                // editEvent changes for events that ARE on the server
                const editEventChanges = changes.filter(
                    (c) => c.type === 'editEvent' && !creatingEventIds.has(c.eventId),
                );

                // Skip crew edits for events that haven't been created on the server yet.
                // Those events will be created with the correct crew state via the
                // createCalendarEvent mutation (attendees are passed in the create input).
                const isServerEvent = (id: string) => id && !creatingEventIds.has(id);

                const trackChange = (eventId: string, changeId: string) => {
                    if (!changeIdsByEvent.has(eventId)) {
                        changeIdsByEvent.set(eventId, []);
                    }
                    if (!changeIdsByEvent.get(eventId)!.includes(changeId)) {
                        changeIdsByEvent.get(eventId)!.push(changeId);
                    }
                };

                // Changes that are no-ops (skipped because they target synthetic events
                // or are otherwise not sent to the server) should be treated as succeeded.
                const noOpChangeIds: string[] = [];
                // CR f0c54df1/24702024 (!2639): corrupt changes (missing/empty event ids)
                // must stay QUEUED as failed — marking them as no-ops would silently
                // drop them without ever reaching the server, and submitting only the
                // valid entries of a multi-event change would leave it half-applied.
                // Collected here, marked failed once failedChangeIdsSoFar exists.
                const malformedChangeIds: string[] = [];

                for (const change of changes) {
                    if (change.type === 'createEvent') continue; // Events are created via createCalendarEvent
                    if (change.type === 'deleteEvent') continue; // Events are deleted via removeCalendarEvent
                    // Notes changes save through their own paths below:
                    if (change.type === 'editEvent' && change.eventData?.opsNotes !== undefined) continue; // updateJobGQL
                    if (change.type === 'editEvent' && change.eventData?.eventNotes !== undefined) continue; // bulkEdit metadata

                    if (change.type === 'moveCrew') {
                        // CR 24702024: validate the WHOLE moveCrew change before building
                        // its edits — a malformed (non-string/empty) id anywhere means the
                        // source/destination pair is not trustworthy; submitting only the
                        // valid side leaves the move half-applied (crew on both events)
                        // and a retry would replay it. temp-* ids stay exempt: they are
                        // pending-create ids whose crew arrives via the create payload.
                        const moveEntries = [ ...(change.fromEvents || []), ...(change.toEvents || []) ];
                        const moveMalformed = moveEntries.some(
                            (entry) => typeof entry?.eventId !== 'string' || entry.eventId === '',
                        );
                        if (moveMalformed) {
                            malformedChangeIds.push(change.id);
                            continue;
                        }
                        let hadServerEvent = false;
                        // Move crew: remove from fromEvents, add to toEvents
                        for (const from of change.fromEvents || []) {
                            if (!isServerEvent(from.eventId)) continue;
                            hadServerEvent = true;
                            if (!editsByEvent.has(from.eventId)) {
                                editsByEvent.set(from.eventId, {
                                    id: from.eventId,
                                    edit: { setAttendees: { addAttendees: [], removeAttendees: [] } },
                                });
                            }
                            editsByEvent.get(from.eventId).edit.setAttendees.removeAttendees.push({
                                userId: change.userId,
                                role: from.role || EventAttendeeRoles.crewMember,
                            });
                            trackChange(from.eventId, change.id);
                        }
                        for (const to of change.toEvents || []) {
                            if (!isServerEvent(to.eventId)) continue;
                            hadServerEvent = true;
                            if (!editsByEvent.has(to.eventId)) {
                                editsByEvent.set(to.eventId, {
                                    id: to.eventId,
                                    edit: { setAttendees: { addAttendees: [], removeAttendees: [] } },
                                });
                            }
                            editsByEvent.get(to.eventId).edit.setAttendees.addAttendees.push({
                                userId: change.userId,
                                role: change.role || EventAttendeeRoles.crewMember,
                                locked: false,
                            });
                            trackChange(to.eventId, change.id);
                        }
                        if (!hadServerEvent) noOpChangeIds.push(change.id);
                        continue;
                    }

                    // removeCrew with fromEvents: remove from all listed events
                    if (change.type === 'removeCrew' && change.fromEvents?.length) {
                        // Same whole-change validation as moveCrew (CR 24702024): a
                        // malformed id in the from-list must not silently reduce the
                        // removal set.
                        const removeMalformed = (change.fromEvents).some(
                            (entry) => typeof entry?.eventId !== 'string' || entry.eventId === '',
                        );
                        if (removeMalformed) {
                            malformedChangeIds.push(change.id);
                            continue;
                        }
                        let hadServerEvent = false;
                        for (const from of change.fromEvents) {
                            if (!isServerEvent(from.eventId)) continue;
                            hadServerEvent = true;
                            if (!editsByEvent.has(from.eventId)) {
                                editsByEvent.set(from.eventId, {
                                    id: from.eventId,
                                    edit: { setAttendees: { addAttendees: [], removeAttendees: [] } },
                                });
                            }
                            editsByEvent.get(from.eventId).edit.setAttendees.removeAttendees.push({
                                userId: change.userId,
                                role: from.role || EventAttendeeRoles.crewMember,
                            });
                            trackChange(from.eventId, change.id);
                        }
                        if (!hadServerEvent) noOpChangeIds.push(change.id);
                        continue;
                    }

                    // CR f0c54df1: a restored change with a corrupt/missing event id is
                    // NOT a pending-create no-op — keep it queued as failed instead of
                    // silently dropping it with the no-op classification.
                    if (typeof change.eventId !== 'string' || !change.eventId) {
                        malformedChangeIds.push(change.id);
                        continue;
                    }

                    if (!isServerEvent(change.eventId)) {
                        noOpChangeIds.push(change.id);
                        continue;
                    }

                    if (!editsByEvent.has(change.eventId)) {
                        editsByEvent.set(change.eventId, {
                            id: change.eventId,
                            edit: {
                                setAttendees: {
                                    addAttendees: [],
                                    removeAttendees: [],
                                },
                            },
                        });
                    }

                    const edit = editsByEvent.get(change.eventId);
                    if (change.type === 'addCrew') {
                        edit.edit.setAttendees.addAttendees.push({
                            userId: change.userId,
                            role: change.role || EventAttendeeRoles.crewMember,
                            locked: (change as any).locked ?? false,
                        });
                    } else if (change.type === 'removeCrew') {
                        edit.edit.setAttendees.removeAttendees.push({
                            userId: change.userId,
                            role: change.role || EventAttendeeRoles.crewMember,
                        });
                    }
                    trackChange(change.eventId, change.id);
                }

                // Cancel out conflicting add+remove for the same user on the same event.
                // If a user was added then removed (or vice versa), drop both entries —
                // the net effect is no change, but the backend would remove then re-add.
                // NOTE: `edits` is snapshotted AFTER the editEventChanges loop below
                // so note-only changes (which create their editsByEvent entry there)
                // are included in the bulk mutation.
                for (const edit of editsByEvent.values()) {
                    const sa = edit.edit.setAttendees;
                    if (!sa?.addAttendees?.length || !sa?.removeAttendees?.length) continue;
                    const removeIds = new Set(sa.removeAttendees.map((a: any) => a.userId));
                    const addIds = new Set(sa.addAttendees.map((a: any) => a.userId));
                    const cancelIds = new Set(
                        [...removeIds].filter((id) => addIds.has(id)),
                    );
                    if (cancelIds.size === 0) continue;
                    sa.addAttendees = sa.addAttendees.filter((a: any) => !cancelIds.has(a.userId));
                    sa.removeAttendees = sa.removeAttendees.filter((a: any) => !cancelIds.has(a.userId));
                }

                // Add editEvent changes (title/time) — merge into existing
                // editsByEvent entries instead of creating separate edits
                // for the same event ID. All metadata contributions
                // (crewRequirement / dayNotes / opsTaskFields) merge into ONE
                // metadata object per event so later branches can't clobber
                // earlier ones.
                for (const editChange of editEventChanges) {
                    const touched = editChange.newTitle
                        || editChange.newStart
                        || editChange.newEnd
                        || editChange.eventData?.opsTaskFields
                        || editChange.eventData?.dayNotes !== undefined
                        || editChange.eventData?.eventNotes !== undefined
                        || editChange.eventData?.eventAttachments !== undefined
                        || editChange.eventData?.opsTaskStatus !== undefined;
                    if (touched) {
                        if (!editsByEvent.has(editChange.eventId)) {
                            editsByEvent.set(editChange.eventId, {
                                id: editChange.eventId,
                                edit: {},
                            });
                        }
                        const edit = editsByEvent.get(editChange.eventId);
                        if (editChange.newTitle) edit.edit.title = editChange.newTitle;
                        if (editChange.newStart) edit.edit.start = editChange.newStart;
                        if (editChange.newEnd) edit.edit.end = editChange.newEnd;
                        if (editChange.eventData?.opsTaskStatus !== undefined) {
                            edit.edit.status = editChange.eventData.opsTaskStatus;
                        }

                        const hasMetaEdits = editChange.eventData?.crewRequirement
                            || editChange.eventData?.dayNotes !== undefined
                            || editChange.eventData?.eventNotes !== undefined
                            || editChange.eventData?.eventAttachments !== undefined
                            || editChange.eventData?.opsTaskFields;
                        if (hasMetaEdits) {
                            // Compose the metadata object once per event, starting
                            // from what THIS batch already accumulated for it (a
                            // second edit of the same event in one save replaces
                            // edit.edit.metadata wholesale — starting from the
                            // server snapshot again would drop the first change,
                            // e.g. attachments set before a notes edit) and
                            // falling back to the event's server metadata.
                            const mergedMeta: Record<string, unknown> = edit.edit.metadata
                                ? (() => { try { return JSON.parse(edit.edit.metadata); } catch { return null; } })()
                                    ?? { ...(eventMetadataMap.get(editChange.eventId) || {}) }
                                : { ...(eventMetadataMap.get(editChange.eventId) || {}) };

                            if (editChange.eventData?.crewRequirement) {
                                mergedMeta.crewRequirement = JSON.stringify(editChange.eventData.crewRequirement);
                            }
                            if (editChange.eventData?.dayNotes !== undefined) {
                                mergedMeta.dayNotes = editChange.eventData.dayNotes;
                            }
                            if (editChange.eventData?.eventNotes !== undefined) {
                                mergedMeta.eventNotes = editChange.eventData.eventNotes;
                            }
                            if (editChange.eventData?.eventAttachments !== undefined) {
                                // JSON-string like crewRequirement above — Neo4j
                                // metadata values must stay primitive. The
                                // frontend read tolerates both shapes (#6983:
                                // Event Notes 📎, event-scoped).
                                mergedMeta.eventAttachments = JSON.stringify(editChange.eventData.eventAttachments);
                            }
                            if (editChange.eventData?.opsTaskFields) {
                                Object.assign(mergedMeta, opsTaskFieldsToMetadata(editChange.eventData.opsTaskFields));
                                if (editChange.eventData.opsTaskFields.title) {
                                    edit.edit.title = editChange.eventData.opsTaskFields.title;
                                }
                                if (editChange.eventData.opsTaskFields.notes !== undefined) {
                                    // Card fallback text: plain text of the notes delta.
                                    edit.edit.description = opsTaskNotesPlainText(editChange.eventData.opsTaskFields.notes);
                                }
                            }
                            edit.edit.metadata = JSON.stringify(mergedMeta);
                        }
                        trackChange(editChange.eventId, editChange.id);
                    }
                }

                // Snapshot the bulk-edit inputs AFTER the editEventChanges loop —
                // note-only changes create their editsByEvent entry there, and
                // snapshotting earlier would silently skip the mutation for them
                // (the change would be marked saved without persisting the note).
                const edits = Array.from(editsByEvent.values());

                // Remove no-op edits: entries with empty setAttendees and no other edit fields
                for (let i = edits.length - 1; i >= 0; i--) {
                    const e = edits[i];
                    const sa = e.edit?.setAttendees;
                    const hasAttendeeChanges = (sa?.addAttendees?.length || sa?.removeAttendees?.length);
                    const hasOtherEdits = e.edit?.title || e.edit?.start || e.edit?.end || e.edit?.metadata
                        || e.edit?.description || e.edit?.status;
                    if (!hasAttendeeChanges && !hasOtherEdits) {
                        noOpChangeIds.push(...(changeIdsByEvent.get(e.id) || []));
                        changeIdsByEvent.delete(e.id);
                        edits.splice(i, 1);
                    }
                }

                const allChangeIds = changes.map((c) => c.id);

                // Execute deletes first, then creates, then bulk edits.
                // Track which change IDs actually succeeded so partial failures
                // stay queued and retryable.
                const deleteIds = deleteChanges.map((c) => c.eventId);
                const deleteChangeIds = deleteChanges.map((c) => c.id);
                // Change IDs whose mutation failed — these stay queued and
                // retryable; everything else in allChangeIds succeeded.
                const failedChangeIdsSoFar = new Set<string>();
                // CR f0c54df1/24702024: corrupt changes collected by the edit builder
                // stay queued (failed/retryable) — they never reached the server.
                malformedChangeIds.forEach((id) => failedChangeIdsSoFar.add(id));

                const delete$ = deleteIds.length > 0
                    ? removeCalendarEventGQL.mutate({ ids: deleteIds })
                        .pipe(
                            map(() => deleteChangeIds),
                            catchError((err) => {
                                notify.apolloError('Failed to delete blocks', err);
                                deleteChangeIds.forEach((id) => failedChangeIdsSoFar.add(id));
                                return of([] as string[]);
                            }),
                        )
                    : of([] as string[]);

                // Save ops notes (editEvent changes with eventData.opsNotes)
                // Deduplicate: only keep the latest ops-notes change per event/jobId
                const opsNotesChanges = changes.filter(
                    (c) => c.type === 'editEvent' && c.eventData?.opsNotes !== undefined,
                );
                const latestOpsNotesByJobId = new Map<string, any>();
                for (const c of opsNotesChanges) {
                    const jobId = c.eventData.jobId;
                    if (!jobId) continue;
                    latestOpsNotesByJobId.set(jobId, c);
                }
                const dedupedOpsNotes = Array.from(latestOpsNotesByJobId.values());
                const opsNotes$ = dedupedOpsNotes.length > 0
                    ? forkJoin(dedupedOpsNotes.map((c) =>
                        updateJobGQL.mutate({
                            updateJobs: {
                                jobId: c.eventData.jobId,
                                metadata: { calculatorOutput__opsNotes: c.eventData.opsNotes },
                            },
                        } as any).pipe(
                            map(() => c.id),
                            catchError((err) => {
                                notify.apolloError('Failed to save ops notes', err);
                                failedChangeIdsSoFar.add(c.id);
                                return of(null);
                            }),
                        ),
                      )).pipe(map((results: any) => results.filter((r: any) => r !== null) as string[]))
                    : of([] as string[]);

                // Event notes (editEvent changes with eventData.eventNotes) are
                // saved through the bulkEdit metadata path (event
                // metadata.eventNotes) — no separate crewSummary mutation.

                const createChangeIds = createChanges.map((c) => c.id);
                const create$ = createOps.length > 0
                    ? createCalendarEventGQL.mutate({ calendarEvents: createOps })
                        .pipe(
                            map(() => createChangeIds),
                            catchError((err) => {
                                notify.apolloError('Failed to create blocks', err);
                                createChangeIds.forEach((id) => failedChangeIdsSoFar.add(id));
                                // Edits merged into a failed create were never
                                // persisted — retain them so the retry submits
                                // the create WITH its notes/attachments
                                // (CodeRabbit).
                                //
                                // Changed flows of this retention (CodeRabbit
                                // round 17), for anyone touching it:
                                //   - RETRY: the merged day-notes, event-notes
                                //     and attachment edits STAY in the pending
                                //     queue and resubmit together with the
                                //     create — they are not lost and not
                                //     silently dropped.
                                //   - DISCARD: the queued edits ride WITH the
                                //     failed create (no server event exists to
                                //     bulk-edit) — discarding removes them
                                //     together with it.
                                for (const change of createChanges) {
                                    for (const editId of editsMergedIntoCreate.get(change.eventId) ?? []) {
                                        failedChangeIdsSoFar.add(editId);
                                    }
                                }
                                return of([] as string[]);
                            }),
                        )
                    : of([] as string[]);

                // Malformed-edit guard (#7277): an edit entry without a usable
                // id can't be addressed by the bulk mutation, and the eager
                // change-ID collection below used to throw
                // "Cannot read properties of undefined (reading 'id')"
                // outside catchError (Ray's repeatable "Failed to save edits"
                // toast — this effect saves schedule blocks from the day view
                // too). Drop the malformed entries and mark their change IDs
                // failed so they stay queued and retryable — the same
                // retention semantics as the failed-create block above.
                for (let i = edits.length - 1; i >= 0; i--) {
                    const e = edits[i];
                    if (!e || typeof e.id !== 'string' || !e.id) {
                        for (const id of (changeIdsByEvent.get(e?.id) ?? [])) {
                            failedChangeIdsSoFar.add(id);
                        }
                        edits.splice(i, 1);
                    }
                }

                // Collect all change IDs that the bulk edit represents
                const allEditChangeIds = edits.flatMap((e: any) => changeIdsByEvent.get(e?.id) || []);
                const edit$ = edits.length > 0
                    ? bulkEditCalendarEventGQL.mutate({ edits }, { context: generateMutationContext() })
                        .pipe(
                            map((res: any) => {
                                const { total = 0 } = res?.data?.bulkEditCalendarEvent || {};
                                if (total === edits.length) {
                                    return [...allEditChangeIds, ...noOpChangeIds];
                                }
                                allEditChangeIds.forEach((id: string) => failedChangeIdsSoFar.add(id));
                                throw new Error(`Bulk edit only applied ${total}/${edits.length} edits`);
                            }),
                            catchError((err) => {
                                notify.apolloError('Failed to save edits', err);
                                allEditChangeIds.forEach((id: string) => failedChangeIdsSoFar.add(id));
                                return of([] as string[]);
                            }),
                        )
                    : of([...noOpChangeIds]);

                // Execute saves in order: deletes → ops notes → creates →
                // bulk edits (event notes ride the bulk-edit metadata path).
                // concat() keeps the chain flat so the
                // success/partial computation stays readable. last() emits the
                // edit$ result; each stream has already recorded its failures
                // in failedChangeIdsSoFar.
                return concat(
                    delete$,
                    opsNotes$,
                    create$,
                    edit$,
                ).pipe(
                    last(),
                    map(() => {
                        const succeededIds = allChangeIds.filter((id) => !failedChangeIdsSoFar.has(id));
                        const failedIds = allChangeIds.filter((id) => failedChangeIdsSoFar.has(id));
                        if (failedIds.length === 0) {
                            notify.success(`Saved ${changes.length} change(s).`);
                            return DispatchActions.saveChangesSuccess({
                                changeIds: allChangeIds,
                            });
                        }
                        if (succeededIds.length > 0) {
                            notify.success(`Saved ${succeededIds.length} of ${changes.length} change(s).`);
                        }
                        return DispatchActions.saveChangesPartialSuccess({
                            succeededChangeIds: succeededIds,
                            failedChangeIds: failedIds,
                        });
                    }),
                    catchError((error) => {
                        notify.apolloError('Failed to save changes', error);
                        return of(DispatchActions.saveChangesError({ error }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true },
);

// ── Discard pending changes ─────────────────────────────────────────────────
// Clear the queue and hard refresh to get the true state from the server
export const DiscardPendingChanges = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DispatchActions.discardChangesClicked),
            map(() => DispatchActions.changesDiscarded()),
        );
    },
    { functional: true, dispatch: true },
);

export const RefreshAfterDiscard = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DispatchActions.changesDiscarded),
            map(() => DispatchActions.hardRefreshEvents({ date: null })),
        );
    },
    { functional: true, dispatch: true },
);

// ── Replay pending changes after refresh ────────────────────────────────────
// When events are reloaded from the server, re-apply pending crew changes
// on top of the fresh data so the user's unsaved edits are preserved.
export const ReplayPendingChangesAfterRefresh = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        // Replay fires when pending changes are loaded from localStorage
        // (after page reload) — at that point events are already in the store.
        // Also fires on saveChangesSuccess (remaining changes after partial save).
        return actions$.pipe(
            ofType(
                DispatchActions.loadPendingChangesFromStorage,
                DispatchActions.saveChangesSuccess,
            ),
            concatLatestFrom(() => store.select(selectPendingChanges)),
            filter(([, changes]) => changes.length > 0),
            mergeMap(([, changes]): Action[] => {
                // Build optimistic update edits from pending changes
                const edits: UpdateCrewActionPayload[] = [];
                const isServerEvent = (id: string) => id && !id.startsWith('temp-');
                for (const change of changes) {
                    if (change.type === 'addCrew') {
                        if (!isServerEvent(change.eventId)) continue;
                        edits.push({
                            eventId: change.eventId,
                            eventName: change.eventName,
                            addAttendees: [{
                                user: { id: change.userId },
                                name: change.userName,
                                role: change.role || EventAttendeeRoles.crewMember,
                            } as any],
                        });
                    } else if (change.type === 'removeCrew') {
                        if (!isServerEvent(change.eventId)) continue;
                        edits.push({
                            eventId: change.eventId,
                            eventName: change.eventName,
                            removeAttendees: [{
                                user: { id: change.userId },
                                role: change.role || EventAttendeeRoles.crewMember,
                            } as any],
                        });
                    } else if (change.type === 'moveCrew') {
                        for (const from of change.fromEvents || []) {
                            if (!isServerEvent(from.eventId)) continue;
                            edits.push({
                                eventId: from.eventId,
                                eventName: from.eventName,
                                removeAttendees: [{
                                    user: { id: change.userId },
                                    role: from.role || EventAttendeeRoles.crewMember,
                                } as any],
                            });
                        }
                        for (const to of change.toEvents || []) {
                            if (!isServerEvent(to.eventId)) continue;
                            edits.push({
                                eventId: to.eventId,
                                eventName: to.eventName,
                                addAttendees: [{
                                    user: { id: change.userId },
                                    name: change.userName,
                                    role: change.role || EventAttendeeRoles.crewMember,
                                } as any],
                            });
                        }
                    }
                }
                // Crew ops replay through updateCrew; pending event notes must
                // ALSO be re-applied onto the fresh server events (notes live
                // on event metadata.eventNotes which the refresh just
                // replaced) — mixed queues need both, so notes replay is
                // dispatched first and the crew update rides on top of it.
                const replayed: Action[] = [];
                if (changes.some((c) => c.type === 'editEvent' && c.eventData?.eventNotes !== undefined)) {
                    replayed.push(DispatchActions.replayPendingCrewNotes({ changes, events: [] }));
                }
                if (edits.length > 0) {
                    replayed.push(DispatchActions.updateCrew({ edits }));
                }
                return replayed;
            }),
        );
    },
    { functional: true, dispatch: true },
);

// ── Default blocks (calendarEvents.defaultBlocks) ────────────────────────────
// Load the zone's default blocks config when the page is used. Triggers on
// componentHardRefreshed (zone change, manual refresh, schedule-map load)
// AND on the first hardRefreshEvents (the dispatch component's first load
// dispatches hardRefreshEvents, not componentHardRefreshed). Later
// hardRefreshEvents are date changes / refresh-after-save — the zone config
// can't have changed, so only the first is acted on. The gateway scopes the
// query to the user's session zone and returns the parent-zone value as
// fallback, so no zone variables are needed.
export const LoadDefaultBlocksConfig = createEffect(
    (
        actions$ = inject(Actions),
        getConfigValuesGQL = inject(GetConfigValuesGQL),
    ) => {
        return actions$.pipe(
            ofType(
                DispatchActions.componentHardRefreshed,
                DispatchActions.hardRefreshEvents,
            ),
            filter((action, index) =>
                action.type === DispatchActions.componentHardRefreshed.type
                || (action.type === DispatchActions.hardRefreshEvents.type && index === 0)
            ),
            switchMap(() => {
                return getConfigValuesGQL.fetch({
                    keys: ['calendarEvents.defaultBlocks'],
                }).pipe(
                    map((res) => {
                        const configValue = res?.data?.getConfigValues?.[0];
                        // The zone-scoped value wins; the fallback (parent zone)
                        // covers zones that never set their own.
                        const raw = configValue?.value ?? configValue?.fallback?.value ?? null;
                        const entries = parseDefaultBlocksConfig(raw as any);
                        return DispatchActions.defaultBlocksConfigLoaded({ entries });
                    }),
                    catchError((error) => {
                        console.error('Failed to load default blocks config', error);
                        return of(DispatchActions.defaultBlocksConfigLoadError({ error }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true },
);

/**
 * Ensure the default blocks exist for the dispatch date.
 *
 * Fires whenever events finish loading and the config is available. Skips when:
 * - the date was already ensured (hard refresh of the same day must not
 *   duplicate pending creates),
 * - a save is in flight (the events list is optimistic then; the save-success
 *   refresh re-triggers this effect with fresh server data),
 * - the user has pending block creates/deletes of their own (mid-edit).
 *
 * Matching (findMissingDefaultBlocks) treats an existing same-type event as
 * satisfying a default when it carries the config's title (a generated shell
 * is stamped with it) — a hand-added "Ops Manager" block is never duplicated
 * by the config.
 */
export const EnsureDefaultBlocksOnLoad = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            // Ensure runs when events land AND when the config lands —
            // whichever resolves second (a slow config query would otherwise
            // silently skip the ensure until the next refresh).
            ofType(
                DispatchActions.eventsLoaded,
                DispatchActions.defaultBlocksConfigLoaded,
            ),
            concatLatestFrom(() => [
                store.select(selectDefaultBlocksConfig),
                store.select(selectDispatchDate),
                store.select(selectIsSavingChanges),
                store.select(selectDefaultBlocksEnsuredDates),
                store.select(selectEvents),
                store.select(selectEventsLoaded),
            ]),
            // Events must be LOADED — the config-load trigger can fire while
            // the events query is still in flight (config is the lighter
            // query), and matching against an empty events list would queue
            // phantom creates that duplicate the real events.
            filter(([, { entries, loading }, , isSaving, , , eventsLoaded]) =>
                !loading && entries.length > 0 && !isSaving && eventsLoaded,
            ),
            map(([, { entries }, dispatchDate, , ensuredDates, events]) => {
                const isoDate = toLocalIsoDateString(dispatchDate);
                if (ensuredDates.includes(isoDate)) return null;

                // Match only against events that START on the dispatch date.
                // The query window is [dayStart, dayEnd] on start OR end, so an
                // overnight block (22:00 day X → 06:00 X+1) is returned when
                // viewing day X+1 — without this filter it would wrongly
                // satisfy the config for day X+1, creating overnight blocks on
                // alternating days only.
                const dayStart = dayjs(dispatchDate).startOf('day').unix();
                const dayEnd = dayjs(dispatchDate).endOf('day').unix();
                const eventsStartingThatDay = events.filter((e) =>
                    (e.start ?? 0) >= dayStart && (e.start ?? 0) <= dayEnd);

                const missing = findMissingDefaultBlocks(
                    entries,
                    eventsStartingThatDay,
                );
                return { isoDate, missing, dispatchDate };
            }),
            filter((result): result is { isoDate: string; missing: DefaultBlockConfig[]; dispatchDate: Date } =>
                result !== null,
            ),
            map(({ isoDate, missing, dispatchDate }) => {
                const inputs = missing.map((entry) => {
                    const built = buildDefaultBlockInput(entry, dispatchDate);
                    return {
                        eventId: built.eventId,
                        blockType: entry.blockType,
                        name: entry.name,
                        start: built.start,
                        end: built.end,
                        crewRequirement: built.crewRequirement,
                        metadata: built.metadata,
                    };
                });
                return DispatchActions.missingDefaultBlocksEnsured({ date: isoDate, inputs });
            }),
        );
    },
    { functional: true, dispatch: true },
);

/**
 * Queue the blocks created by EnsureDefaultBlocksOnLoad and ResetDefaultBlocks
 * as pending changes — one pendingChangeAdded per block so the existing
 * autosave, save, and localStorage persistence machinery treats config-created
 * blocks exactly like manually created ones.
 *
 * Race-proof: two eventsLoaded can arrive in the same tick (Apollo query
 * batching) and both ensure runs pass the effect's store checks before either
 * commits. Dedupe here against already-queued creates of the same
 * type+name+date — the queue is committed synchronously, so the second batch
 * always sees the first. Creates whose pending create is being deleted by the
 * same reset are exempt from dedupe (replacement, not duplicate).
 */
export const QueueDefaultBlockCreates = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(
                DispatchActions.missingDefaultBlocksEnsured,
                DispatchActions.defaultBlocksResetConfirmed,
            ),
            // For reset, the pending-change dedupe must run AFTER this reset's
            // blockDeleted actions removed the old creates from the queue —
            // otherwise a double reset filters out its own re-creates.
            concatLatestFrom(() => [
                store.select(selectPendingChanges),
                store.select(selectDispatchDate),
            ]),
            mergeMap(([action, pendingChanges, dispatchDate]) => {
                // The ensure action carries the date it covered; reset covers
                // the board's dispatch date.
                const isoDate = (action as { date?: string }).date
                    ?? toLocalIsoDateString(dispatchDate);
                // For reset, creates matching pending creates that are also in
                // this reset's delete list must NOT be deduped — the old
                // create is being replaced. Only skip creates for events that
                // are NOT being deleted.
                const deleteEventIds = new Set(
                    (action as { deleteEventIds?: string[] }).deleteEventIds || [],
                );
                const seenKeys = new Set(
                    pendingChanges
                        .filter((c) => c.type === 'createEvent' && !deleteEventIds.has(c.eventId))
                        .map((c) => `${isoDate}:${c.eventData?.eventType}:${c.eventName}`),
                );
                const newInputs = action.inputs.filter((input) =>
                    !seenKeys.has(`${isoDate}:${input.blockType}:${input.name}`),
                );
                return newInputs.map((input) => {
                    const change: DispatchPendingChange = {
                        id: `create-${input.eventId}`,
                        type: 'createEvent',
                        eventId: input.eventId,
                        eventName: input.name,
                        eventData: {
                            ...input,
                            id: input.eventId,
                            eventType: input.blockType,
                            description: input.name,
                        },
                    };
                    return DispatchActions.pendingChangeAdded({ change });
                });
            }),
        );
    },
    { functional: true, dispatch: true },
);

/**
 * Queue the deletes from a default-blocks reset (blockDeleted per id — the
 * reducer drops the event and queues the deleteEvent change).
 */
export const QueueDefaultBlockDeletes = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DispatchActions.defaultBlocksResetConfirmed),
            filter(({ deleteEventIds }) => deleteEventIds.length > 0),
            mergeMap(({ deleteEventIds }) => deleteEventIds.map((eventId) =>
                DispatchActions.blockDeleted({ eventId }),
            )),
        );
    },
    { functional: true, dispatch: true },
);
/**
 * Reset button — delete every auto-generated block on the board for the
 * dispatch date and re-create all config entries from scratch. Manual blocks
 * (no autoGenerated flag) are left untouched.
 */
export const ResetDefaultBlocks = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        notify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.defaultBlocksResetClicked),
            concatLatestFrom(() => [
                store.select(selectAutoGeneratedBlocks),
                store.select(selectDefaultBlocksConfig),
                store.select(selectDispatchDate),
            ]),
            exhaustMap(([, autoBlocks, { entries }, dispatchDate]) => {
                if (entries.length === 0) {
                    notify.info('No default blocks configured for this zone.');
                    return EMPTY;
                }

                const deleteEventIds = autoBlocks.map((b) => b.id);
                const inputs = entries.map((entry) => {
                    const built = buildDefaultBlockInput(entry, dispatchDate);
                    return {
                        eventId: built.eventId,
                        blockType: entry.blockType,
                        name: entry.name,
                        start: built.start,
                        end: built.end,
                        crewRequirement: built.crewRequirement,
                        metadata: built.metadata,
                    };
                });

                if (deleteEventIds.length === 0 && inputs.length === 0) {
                    return EMPTY;
                }

                notify.info(
                    deleteEventIds.length > 0
                        ? `Resetting ${deleteEventIds.length} auto-generated block(s)…`
                        : 'Creating default blocks…',
                );
                return of(DispatchActions.defaultBlocksResetConfirmed({
                    deleteEventIds,
                    inputs,
                }));
            }),
        );
    },
    { functional: true, dispatch: true },
);

// ── localStorage persistence for send-dialog crew notes (#7283 D) ─────────────
// The send dialog already mirrors every keystroke into the store
// (sendScheduleCrewCustomNoteChanged on onCrewNoteChange); mirroring the
// map to localStorage makes typed notes survive a board reload — client-side
// only per the #7283 ruling (the backend-stamped post-send note is another,
// authoritative store).
//
// The mirror is scoped to the dispatch day it was typed on (CodeRabbit
// round 4): a fresh page session opened on ANOTHER day must not surface
// yesterday's drafts, so the payload carries its day and the restore
// effect drops cross-day entries.
function crewNotesMirrorStorageKey(): string {
    return dispatchStorageKey('dispatch-send-schedule-crew-notes');
}

interface CrewNotesMirror {
    /** YYYY-MM-DD the drafts were typed on. */
    day: string;
    notes: Record<string, string>;
}

function readCrewNotesMirror(key: string): CrewNotesMirror | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) { return null; }
        const parsed = JSON.parse(raw) as CrewNotesMirror;
        if (!parsed || typeof parsed.day !== 'string'
            || !parsed.notes || typeof parsed.notes !== 'object') {
            return null;
        }
        return parsed;
    } catch { return null; }
}

function writeCrewNotesMirror(key: string, mirror: CrewNotesMirror): void {
    try {
        const hasNotes = Object.values(mirror.notes || {})
            .some((v) => typeof v === 'string' && v.trim());
        if (hasNotes && mirror.day) {
            localStorage.setItem(key, JSON.stringify(mirror));
        } else {
            localStorage.removeItem(key);
        }
    } catch { /* localStorage might be full or unavailable */ }
}

/**
 * #7283 D (CodeRabbit ead65a67): sent drafts are cleared only when EVERY
 * route of a send batch reported back successfully. A per-route clear would
 * starve a later in-flight route (or a failed-route retry) of the note.
 */
export const BatchDraftCleanup = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) => {
        return actions$.pipe(
            ofType(
                DispatchActions.sendScheduleSubmitSuccess,
                DispatchActions.sendScheduleSubmitError,
            ),
            concatLatestFrom(() => store.select(selectSendScheduleBatches)),
            map(([action, batches]) => {
                const batch = (batches as Record<string, { pending: number; hadError: boolean; sentUserIds: string[] }>)[action.batchId];
                if (!batch || batch.pending > 0 || batch.hadError) { return { type: 'NO_OP' }; }
                return DispatchActions.sendScheduleCrewDraftsClearSent({
                    batchId: action.batchId,
                    sentUserIds: batch.sentUserIds,
                });
            }),
            filter((action) => action.type !== 'NO_OP'),
        );
    },
    { functional: true, dispatch: true }
);

export const PersistSendScheduleCrewNotes = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) => {
        const mirrorKey = crewNotesMirrorStorageKey();
        // The day this effect last observed: the wipe fires only when the day
        // ACTUALLY changes (CodeRabbit round 3). Module-scoped per app
        // instance; starts empty so the very first load never wipes.
        let lastSeenDay = '';
        return actions$.pipe(
            ofType(
                DispatchActions.sendScheduleCrewCustomNoteChanged,
                DispatchActions.sendScheduleCrewDraftsClearSent,
                DispatchActions.setDispatchDate,
                DispatchActions.hardRefreshEvents,
            ),
            concatLatestFrom(() => [
                store.select(selectSendScheduleCrewNotes),
                store.select(selectDispatchDate),
            ]),
            tap(([action, notes, date]) => {
                try {
                    const type = (action?.type ?? '') as string;
                    const day = date ? dayjs(date).format('YYYY-MM-DD') : '';
                    // #7283 D (CodeRabbit round 3): the mirror wipe fires ONLY
                    // when the day ACTUALLY changes — loadEvents() runs a hard
                    // refresh on every page load (initialization) and the
                    // live-update handlers dispatch more; erasing the mirror
                    // on either would defeat the local persistence the ruling
                    // calls for. The initial observation (lastSeenDay === '')
                    // never wipes, so page loads keep drafts.
                    const dateAction = type === DispatchActions.setDispatchDate.type
                        || type === DispatchActions.hardRefreshEvents.type;
                    if (dateAction) {
                        if (lastSeenDay !== '' && day !== lastSeenDay) {
                            localStorage.removeItem(mirrorKey);
                        }
                        lastSeenDay = day;
                        return;
                    }
                    // (CodeRabbit round 4) stamp the drafts with the day they
                    // were typed on. A stored mirror from ANOTHER day is
                    // discarded at write time — after a real day switch the
                    // live reducer map was already cleared, so the incoming
                    // notes are the fresh day's truth.
                    if (type === DispatchActions.sendScheduleCrewDraftsClearSent.type) {
                        // Batch completed all-success: the reducer dropped the
                        // sent users' drafts and the backend stamped the notes
                        // onto the events. Reconcile the same-day stored mirror
                        // with the live map (stored-only keys — e.g. another
                        // tab's unsent drafts — survive), then remove the sent
                        // users from the result (CodeRabbit rounds 6-7).
                        const sentUserIds = (action as any).sentUserIds as string[];
                        const stored = readCrewNotesMirror(mirrorKey);
                        const storedNotes = stored && stored.day === day ? stored.notes : {};
                        const merged = { ...storedNotes, ...(notes || {}) };
                        for (const userId of sentUserIds || []) { delete merged[userId]; }
                        writeCrewNotesMirror(mirrorKey, { day, notes: merged });
                        return;
                    }
                    const stored = readCrewNotesMirror(mirrorKey);
                    const storedNotes = stored && stored.day === day ? stored.notes : {};
                    const merged = { ...storedNotes, ...(notes || {}) };
                    writeCrewNotesMirror(mirrorKey, { day, notes: merged });
                } catch { /* localStorage might be full or unavailable */ }
            }),
            map(() => null as any),
            filter((v) => v !== null),
        );
    },
    { functional: true, dispatch: false },
);

export const RestoreSendScheduleCrewNotes = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) => {
        const mirrorKey = crewNotesMirrorStorageKey();
        return actions$.pipe(
            ofType(DispatchActions.eventsLoaded),
            concatLatestFrom(() => store.select(selectDispatchDate)),
            map(([, date]) => {
                const viewedDay = date ? dayjs(date).format('YYYY-MM-DD') : '';
                const mirror = readCrewNotesMirror(mirrorKey);
                // (CodeRabbit round 4) a fresh session may open on a day
                // DIFFERENT from where the drafts were typed — restore only
                // same-day drafts, never yesterday's.
                const notes = mirror && viewedDay && mirror.day === viewedDay
                    ? mirror.notes : {};
                return DispatchActions.sendScheduleCrewNotesRestored({ notes });
            }),
        );
    },
    { functional: true, dispatch: true },
);

// ── localStorage persistence for pending changes ─────────────────────────────
const PENDING_CHANGES_STORAGE_KEY = 'dispatch-pending-changes';

// Save to localStorage whenever pending changes change
export const PersistPendingChanges = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) => {
        return actions$.pipe(
            ofType(
                DispatchActions.pendingChangeAdded,
                DispatchActions.pendingChangeRemoved,
                DispatchActions.blockDeleted,
                DispatchActions.saveChangesSuccess,
                DispatchActions.saveChangesPartialSuccess,
                DispatchActions.changesDiscarded,
            ),
            withLatestFrom(store.select(selectPendingChanges)),
            tap(([, changes]) => {
                try {
                    if (changes.length > 0) {
                        localStorage.setItem(PENDING_CHANGES_STORAGE_KEY, JSON.stringify(changes));
                    } else {
                        localStorage.removeItem(PENDING_CHANGES_STORAGE_KEY);
                    }
                } catch { /* localStorage might be full or unavailable */ }
            }),
            map(() => null as any),
            filter((v) => v !== null),
        );
    },
    { functional: true, dispatch: false },
);

// Refresh events after save to pick up server state (bypasses Apollo cache)
// After save, refetch events from network (not Apollo cache) to confirm
// the server state matches our optimistic updates. This is NOT a full page
// reload — just a network-only refetch of the scheduleEvents query.
// After save, optimistic updates in the reducer already reflect changes.
// PersistPendingChanges effect handles localStorage cleanup. No reload needed.

// Load pending changes from localStorage on every eventsLoaded.
// This ensures changes are restored after page reload AND after hard refresh.
// The reducer merges them with existing pending changes (deduped by ID).
export const LoadPendingChangesFromStorage = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DispatchActions.eventsLoaded),
            map(() => {
                try {
                    const raw = localStorage.getItem(PENDING_CHANGES_STORAGE_KEY);
                    if (!raw) return DispatchActions.loadPendingChangesFromStorage({ changes: [] });
                    const changes = JSON.parse(raw) as DispatchPendingChange[];
                    return DispatchActions.loadPendingChangesFromStorage({ changes });
                } catch {
                    return DispatchActions.loadPendingChangesFromStorage({ changes: [] });
                }
            }),
        );
    },
    { functional: true, dispatch: true },
);

export const assignCrewWithAI = createEffect(() => {
    const actions$ = inject(Actions);
    const store = inject(Store);
    const optimizeDateGQL = inject(OptimizeDateGQL);
    const permissionHandler = inject(PermissionService);

    return actions$.pipe(
        ofType(DispatchActions.assignCrewMembersWithAIClicked),
        // Pair the click with the first permission emission; concatLatestFrom
        // drops the action when watchPermission hasn't emitted yet, which
        // would swallow the AI-assignment request entirely.
        concatMap((action) => permissionHandler.watchPermission('find.optimize').pipe(
            take(1),
            map((hasOptimize) => [ action, hasOptimize ] as const),
        )),
        filter(([, hasOptimize]) => hasOptimize),
        withLatestFrom(
            store.select(selectDispatchDate),
        ),
        exhaustMap(([{ }, date]) => {

            return optimizeDateGQL.mutate({
                input: {
                    date: dayjs(date).format('YYYY-MM-DD'),
                    doApply: true,
                }
            }).pipe(
                map((result) => {

                    const output = result?.data?.optimizeDate;

                    if (output.status === 'INFEASIBLE') {
                        // Shown in the assign-crew dialog (not a toast) so the
                        // dispatcher can read every reason and act on it.
                        const reasons = output.infeasibility_reasons?.length
                            ? output.infeasibility_reasons
                            : [ 'You are over capacity or there is an event that cannot be served.' ];

                        return DispatchActions.crewAssignmentInfeasible({ reasons });
                    }

                    return DispatchActions.crewMembersAssignedWithAISuccessfully({ output });
                }),
                catchError((error) => of(DispatchActions.failedToAssignCrewMembersWithAI({ error }))),
            );
        }),
    );
}, { functional: true, dispatch: true });

export const PreSelectCrewScheduleRecepients = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.sendCrewScheduleDialogOpened),
            withLatestFrom(store.select(selectWorkingCrew)),
            map(([_, workingCrew]) =>
                DispatchActions.recepientsForCrewScheduleChanged({ recepients: workingCrew })
            )
        );
    },
    { functional: true, dispatch: true },
);

export const SendCrewSchedule = createEffect(
    (
        actions$ = inject(Actions),
        notifyGQL = inject(NotifyGQL),
        store = inject(Store),
        notify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.crewScheduleSent),
            withLatestFrom(
                store.select(selectTemplateVariableData),
                store.select(selectCrewScheduleRecepients)
            ),
            exhaustMap(([{ contents, subject }, crewData, crewScheduleRecepients]) => {
                const userIds = crewScheduleRecepients.map((crew) => crew.user.id);
                const userData = userIds.map((userId) => {
                    const crewMemberData = crewData.get(userId);
                    return {
                        userId,
                        data: {
                            quillTemplateContents: JSON.stringify(
                                replaceQuillVariablesWithActualValues(contents, crewMemberData)
                            ),
                            subject: convertQuillContentsToText(
                                replaceQuillVariablesWithActualValues(subject, crewMemberData)
                            ),
                        },
                    };
                });

                const input: NotifyMutationVariables = {
                    type: 'dispatchSchedule',
                    users: userData,
                };

                return notifyGQL.mutate(input).pipe(
                    map((res) => {
                        const isSuccess = res.data.notify;
                        if (isSuccess) {
                            notify.success('Crew schedule sent successfully.');
                            return DispatchActions.crewScheduleSendSuccess();
                        }

                        if (res.errors.length) {
                            notify.error('Failed to send crew schedule.');
                            console.error('Filed to send crew schedule.', res.errors);
                            return DispatchActions.crewScheduleSendError();
                        }
                    }),
                    catchError((error) => {
                        notify.apolloError('Failed to send crew schedule.', error);
                        return of(
                            DispatchActions.crewScheduleSendError(),
                        );
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true },
);

export const NavigateCrewSettings = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(
                DispatchActions.nextCrewMemberClicked,
                DispatchActions.previousCrewMemberClicked,
            ),
            withLatestFrom(
                store.select(selectSelectedUserForRateEdit),
                store.select(selectPotentialCrew),
            ),
            map(([action, currentUser, crewList]) => {
                if (!currentUser || !crewList.length) {
                    return { type: 'NO_OP' };
                }

                const currentIndex = crewList.findIndex(c => c.user.id === currentUser.id);
                if (currentIndex === -1) {
                    return { type: 'NO_OP' };
                }

                let nextIndex = currentIndex;
                if (action.type === DispatchActions.nextCrewMemberClicked.type) {
                    nextIndex = (currentIndex + 1) % crewList.length;
                } else {
                    nextIndex = (currentIndex - 1 + crewList.length) % crewList.length;
                }

                return DispatchActions.editCrewSettingsUserSelected({
                    user: crewList[nextIndex].user,
                });
            }),
            filter(action => action.type !== 'NO_OP')
        );
    },
    { functional: true, dispatch: true }
);

export const createCrewMember = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        userService = inject(UserService),
        notify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.createCrewMember),
            withLatestFrom(store.select(selectRoles)),
            exhaustMap(([{ user }, roles]) => {
                const crewRoles = sortBy(
                    roles.filter(r => r.name.includes('Crew Member')),
                    (r) => -r.zones.nodes[0].zoneDepth
                );
                const crewMemberRole = crewRoles[0];

                if (!crewMemberRole) {
                    notify.error('"Crew Member" role not found.');
                    return of(DispatchActions.createCrewMemberError({ error: new Error('Role not found') }));
                }

                return userService.createUsers({
                    users: [user],
                    roles: [crewMemberRole.id],
                    inheritRoleZones: true,
                }).pipe(
                    map((res) => {
                        // Handle potential response structures
                        const createdUser = Array.isArray(res) ? res[0] : (res?.users?.[0] || res);

                        if (!createdUser || !createdUser.id) {
                             console.warn('Unexpected create user response:', res);
                             throw new Error('Failed to create user: No ID returned');
                        }
                        notify.success('Crew member created successfully.');
                        return DispatchActions.createCrewMemberSuccess({ user: createdUser });
                    }),
                    catchError((error) => {
                        console.error('Failed to create crew member.', error);
                        const message = error.message || 'Unknown error';
                        notify.error(`Failed to create crew member: ${message}`);
                        return of(DispatchActions.createCrewMemberError({ error }));
                    })
                );
            })
        );
    },
    { functional: true }
);

export const autoCreateCrewMember = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.updateDraftCrewMember),
            debounceTime(500),
            withLatestFrom(
                store.select(selectSelectedUserForRateEdit),
                store.select(selectIsCreatingUser)
            ),
            filter(([_, user, isCreating]) => {
                const hasFirstName = !!(user?.givenName && user?.givenName.trim().length > 0);
                const hasLastName = !!(user?.familyName && user?.familyName.trim().length > 0);
                const hasId = !!user?.id;
                return hasFirstName && hasLastName && !hasId && !isCreating;
            }),
            map(([_, user]) => {
                return DispatchActions.createCrewMember({ user });
            })
        );
    },
    { functional: true }
);

export const LoadWorkerHistory = createEffect(
    (
        actions$ = inject(Actions),
        scheduleEventsGQL = inject(ScheduleEventsGQL),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(
                DispatchActions.usersLoaded,
                DispatchActions.setDispatchDate,
                DispatchActions.crewBookOffEventCreatedSuccess,
                DispatchActions.crewBookOffEventDeletedSuccess,
            ),
            withLatestFrom(
                store.select(selectDispatchDate),
                store.select(selectUsers)
            ),
            exhaustMap(([ _action, date, users ]) => {
                const userIds = users.map(u => u.id);
                if (!userIds.length) return EMPTY;

                const end = dayjs(date).endOf('day').unix();
                const start = dayjs(date).subtract(7, 'days').startOf('day').unix();

                return scheduleEventsGQL.fetch({
                    filter: {
                        hasAttendees: userIds,
                        min: start,
                        max: end,
                    },
                    limit: -1,
                }, { fetchPolicy: 'network-only' }).pipe(
                    map((res) => {
                        const events = res.data?.calendarEvents?.events || [];
                        return DispatchActions.workerHistoryLoaded({ events });
                    }),
                    catchError((error) => {
                        console.error('Error loading worker history', error);
                        return of(DispatchActions.workerHistoryLoadError({ error }));
                    })
                );
            })
        );
    },
    { functional: true, dispatch: true }
);

// export const TriggerLoadWorkerHistory = createEffect(
//     (
//         actions$ = inject(Actions),
//         store = inject(Store),
//     ) => {
//         return actions$.pipe(
//             ofType(DispatchActions.usersLoaded),
//             withLatestFrom(store.select(DispatchFeature.selectDispatchDate)),
//             map(([{ users }, date]) => {
//                 const userIds = users.map(u => u.id);
//                 return DispatchActions.loadWorkerHistory({ userIds, date });
//             })
//         );
//     },
//     { functional: true, dispatch: true }
// );

export const RemoveAllLocks = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.removeAllLocks),
            withLatestFrom(store.select(selectEvents)),
            map(([_, events]) => {
                const edits: UpdateCrewActionPayload[] = [];
                
                events.forEach(event => {
                    const attendees = event.attendees.filter(a => a.role !== EventAttendeeRoles.author && a.locked);
                    if (attendees.length === 0) return;

                    const addAttendees = attendees.map(a => ({
                        ...a,
                        name: a.user.givenName + ' ' + a.user.familyName,
                        locked: false
                    })) as any;
                    
                    edits.push({
                        eventId: event.id,
                        eventName: event.title,
                        addAttendees: addAttendees,
                        removeAttendees: [] 
                    });
                });
                
                if (edits.length === 0) return { type: 'NO_OP' };
                
                return DispatchActions.updateCrew({ edits });
            }),
            filter(action => action.type !== 'NO_OP')
        );
    },
    { functional: true, dispatch: true }
);

export const UnassignAllUnlockedCrew = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.unassignAllUnlockedCrew),
            withLatestFrom(store.select(selectEvents)),
            map(([_, events]) => {
                // Push removeCrew pending changes for each unlocked attendee
                // on each event, so they go through the save/discard flow.
                const changes: any[] = [];

                for (const event of events) {
                    const unlockedAttendees = event.attendees.filter(
                        (a: any) => !a.locked && a.role !== EventAttendeeRoles.author
                            && a.role !== 'Author',
                    );
                    if (unlockedAttendees.length === 0) continue;

                    for (const attendee of unlockedAttendees) {
                        const userName = [attendee.user?.givenName, attendee.user?.familyName]
                            .filter(Boolean).join(' ');
                        changes.push({
                            id: `unassign-${attendee.user.id}-${event.id}-${Date.now()}`,
                            type: 'removeCrew' as const,
                            eventId: event.id,
                            eventName: event.title,
                            userId: attendee.user.id,
                            userName,
                            role: attendee.role,
                            fromEvents: [{
                                eventId: event.id,
                                eventName: event.title,
                                role: attendee.role,
                            }],
                        });
                    }
                }

                if (changes.length === 0) return { type: 'NO_OP' as const };

                // Dispatch each pending change individually
                for (const change of changes) {
                    store.dispatch(DispatchActions.pendingChangeAdded({ change }));
                }
                return { type: 'NO_OP' as const };
            }),
            filter(action => action.type !== 'NO_OP'),
        );
    },
    { functional: true, dispatch: false }
);

export const LoadDockLocation = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        locationsGQL = inject(LocationsGQL),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.loadDockLocation),
            withLatestFrom(store.select(brandingFeature.selectConfigs)),
            exhaustMap(([_, configs]) => {
                const dockLocationId = configs ? (configs as any)[ZoneConfigKey.FranchiseInfoDocklocationid] : null;

                if (!dockLocationId) {
                    console.warn('Dock location ID not found in config');
                    return EMPTY;
                }

                return locationsGQL.fetch({
                    filter: {
                        ids: [dockLocationId]
                    }
                }).pipe(
                    map(res => {
                        const location = res.data?.locations?.locations?.[0];
                        if (location) {
                            return DispatchActions.setDockLocation({ location });
                        }
                        return { type: 'NO_OP' };
                    }),
                    catchError(err => {
                        console.error('Failed to load dock location', err);
                        return EMPTY;
                    })
                );
            }),
            filter((action: any) => action.type !== 'NO_OP')
        );
    },
    { functional: true, dispatch: true }
);

export const UpdateAssetLocation = createEffect(
    (
        actions$ = inject(Actions),
        updateAssetsGQL = inject(UpdateAssetsGQL),
        notify = inject(FreyaNotificationsService)
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.updateAssetLocation),
            exhaustMap(({ assetId, coordinates }) => {
                return updateAssetsGQL.mutate({
                    assets: [{
                        id: assetId,
                        metadata: {
                            coordinates: JSON.stringify(coordinates)
                        } as any
                    }]
                }).pipe(
                    map(() => {
                        notify.success('Asset location updated');
                        return { type: 'NO_OP' };
                    }),
                    catchError(err => {
                        notify.error('Failed to update asset location');
                        console.error(err);
                        return of({ type: 'NO_OP' });
                    })
                );
            }),
            filter((action: any) => action.type !== 'NO_OP')
        );
    },
    { functional: true, dispatch: false }
);

// ─── Send Schedule to Crew Effects ──────────────────────────────────────────



/** Computes selections from the route tree for "Select Changed / Unsent" or dialog open. */
function buildBulkSelections(
    routeTree: ReturnType<typeof selectSendScheduleRouteTree.projector>,
    selectChangedOnly: boolean,
): Array<{ assetId: string; userId: string; selected: boolean }> {
    const selections: Array<{ assetId: string; userId: string; selected: boolean }> = [];
    for (const route of routeTree as any[]) {
        for (const crewItem of route.crew as any[]) {
            const isChangedOrUnsent = crewItem.sendStatus === 'not-sent'
                || crewItem.sendStatus === 'changed'
                || crewItem.sendStatus === 'removed';
            selections.push({
                assetId: route.assetId,
                userId: crewItem.crewMember.user.id,
                selected: selectChangedOnly
                    ? (isChangedOrUnsent && crewItem.canSend)
                    : false,
            });
        }
    }
    return selections;
}

/**
 * When the Send Schedule dialog opens, auto-select all changed/unsent crew.
 */
export const InitCrewSelectionsOnDialogOpen = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.sendScheduleDialogOpened),
            concatLatestFrom(() => store.select(selectSendScheduleRouteTree)),
            map(([_, routeTree]) => {
                const selections = buildBulkSelections(routeTree as any, true);
                return DispatchActions.sendScheduleBulkSelectionApplied({ selections });
            }),
        );
    },
    { functional: true, dispatch: true }
);

/**
 * "Select Changed / Unsent" button — reads the route tree (which has live sendStatus
 * derived from threads) and marks every changed/unsent crew member as selected.
 * This is an effect rather than a reducer so it can read the computed selector state.
 */
export const SelectAllChangedEffect = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.sendScheduleAllChangedToggled),
            concatLatestFrom(() => store.select(selectSendScheduleRouteTree)),
            map(([_, routeTree]) => {
                const selections = buildBulkSelections(routeTree as any, true);
                return DispatchActions.sendScheduleBulkSelectionApplied({ selections });
            }),
        );
    },
    { functional: true, dispatch: true }
);

/**
 * "Deselect All" button — deselects every crew member by reading the route tree.
 */
export const DeselectAllEffect = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.sendScheduleDeselectAll),
            concatLatestFrom(() => store.select(selectSendScheduleRouteTree)),
            map(([_, routeTree]) => {
                const selections = buildBulkSelections(routeTree as any, false);
                return DispatchActions.sendScheduleBulkSelectionApplied({ selections });
            }),
        );
    },
    { functional: true, dispatch: true }
);

/**
 * When the Send Schedule dialog opens, load threads for all assets in the route tree.
 * Uses forkJoin to dispatch one action per asset (multiple actions expected).
 */
// eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
export const LoadAssetThreadsOnDialogOpen = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        listThreadsGQL = inject(ListAssetScheduleThreadsGQL),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.sendScheduleDialogOpened),
            concatLatestFrom(() => store.select(selectSendScheduleRouteTree)),
            mergeMap(([_, routeTree]) => {
                const assetIds = routeTree.map((r) => r.assetId);
                if (!assetIds.length) { return of(); }

                return forkJoin(
                    assetIds.map((assetId) =>
                        listThreadsGQL.fetch(
                            { objectIds: [assetId], threadMode: ['Send Schedule to Crew'], limit: 10 },
                            { fetchPolicy: 'network-only' },
                        ).pipe(
                            map((res) => {
                                const threads = res.data?.comments?.comments || [];
                                return DispatchActions.sendScheduleAssetThreadsLoaded({ assetId, threads });
                            }),
                            catchError(() => of(DispatchActions.sendScheduleAssetThreadsLoaded({ assetId, threads: [] }))),
                        )
                    )
                );
            }),
            // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
            mergeMap((actions: any[]) => actions),
        );
    },
    { functional: true, dispatch: true }
);

/**
 * E.15 — derive real send-status badges.
 *
 * When a route's threads load (each "Send Schedule to Crew" send stores the
 * per-crew schedule hash in comment metadata.sendResults), recompute every
 * crew member's CURRENT schedule hash and compare:
 * - no hash on file                → 'not-sent'
 * - hash matches last sent         → 'up-to-date'
 * - hash differs / not in last send → 'changed'
 *
 * Runs whenever threads or the derived event set change.
 */
export const DeriveCrewSendStatuses = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) => {
        return actions$.pipe(
            ofType(
                DispatchActions.sendScheduleAssetThreadsLoaded,
                DispatchActions.sendScheduleSubmitSuccess,
                DispatchActions.saveChangesSuccess,
                DispatchActions.eventsLoaded,
            ),
            debounceTime(100),
            concatLatestFrom(() => [
                store.select(selectSendScheduleRouteTree),
                store.select(selectDispatchDate),
            ]),
            // exhaustMap: one derivation at a time; a burst of triggers
            // coalesces into a single run over the latest store state.
            exhaustMap(([, routeTree, dispatchDate]) =>
                from(deriveCrewSendStatusList(routeTree, dispatchDate as Date))
                    .pipe(
                        map((statuses) => statuses.length > 0
                            ? DispatchActions.sendScheduleCrewStatusesLoaded({ statuses })
                            : { type: 'NO_OP' }),
                        filter((action) => action.type !== 'NO_OP'),
                    ),
            ),
        );
    },
    { functional: true, dispatch: true }
);

/**
 * Computes per-crew send statuses by comparing each crew member's current
 * schedule hash against the last hash stored by a send. Returns an empty
 * array when no route has ever been sent to (store default 'not-sent' stands).
 */
async function deriveCrewSendStatusList(
    routeTree: any[],
    dispatchDate: Date,
): Promise<Array<{ userId: string; assetId: string; status: CrewScheduleSendStatus }>> {
    const date = dayjs(dispatchDate).format('YYYY-MM-DD');
    const statuses: Array<{ userId: string; assetId: string; status: CrewScheduleSendStatus }> = [];

    for (const route of routeTree as any[]) {
        const lastSent = extractLastSentHashes(route.threads || []);
        if (lastSent.size === 0) {
            // Nothing ever sent on this route — everyone is 'not-sent',
            // which is the store default; don't spam redundant statuses.
            continue;
        }
        for (const crewItem of route.crew as any[]) {
            const userId = crewItem.crewMember.user.id;
            const crewEvents = (route.events as any[]).filter((e) =>
                (e.crew || []).some((m: any) => m.user?.id === userId),
            );
            // eslint-disable-next-line no-await-in-loop
            const currentHash = await generateCrewScheduleHash(
                userId,
                date,
                crewEvents.map((e) => ({
                    id: e.event.id,
                    start: e.event.start || 0,
                    end: e.event.end || 0,
                    type: e.event.type,
                    locationIds: (e.event.locations || []).map((l: any) => l.id || l.locationId).filter(Boolean),
                    assetIds: (e.event.assets || []).map((a: any) => a.id).filter(Boolean),
                    attendeeIds: (e.event.attendees || []).map((a: any) => a.user?.id || a.userId).filter(Boolean),
                })),
            );
            const sentHash = lastSent.get(userId);
            statuses.push({
                userId,
                assetId: route.assetId,
                // Requirement (E.15): 'up-to-date' means the crew member's current
                // schedule hash matches the hash stored with the last send to
                // THEM. A crew member with no send on file was never messaged —
                // badge them 'not-sent', not 'up-to-date'.
                status: !sentHash
                    ? 'not-sent'
                    : sentHash === currentHash
                        ? 'up-to-date'
                        : 'changed',
            });
        }
    }
    if (!statuses.length) { return []; }
    return statuses;
}

/**
 * Fetch the schedule preview when a crew member is selected for preview.
 */
export const FetchSchedulePreview = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
        previewGQL = inject(PreviewCrewScheduleGQL),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.sendSchedulePreviewRequested),
            // debounce so rapid note edits don't fire a request per keystroke
            debounceTime(300),
            concatLatestFrom(() => [
                store.select(selectDispatchDate),
                store.select(selectSendScheduleAssetNotes),
                store.select(selectSendScheduleCrewNotes),
            ]),
            // switchMap (latest-wins): if a new preview arrives while one is
            // in-flight, cancel the older fetch so the preview always reflects
            // the most recent note content.
            switchMap(([{ assetId, userId }, dispatchDate, assetNotes, crewNotes]) => {
                const date = dayjs(dispatchDate as Date).format('YYYY-MM-DD');
                const customNoteForAsset = (assetNotes as Record<string, string>)[assetId] || undefined;
                const customNoteForCrew = (crewNotes as Record<string, string>)[userId] || undefined;

                return previewGQL.fetch(
                    { assetId, date, userId, customNoteForAsset, customNoteForCrew },
                    { fetchPolicy: 'network-only' },
                ).pipe(
                    map((res) => {
                        const preview = res.data?.previewCrewSchedule;
                        return DispatchActions.sendSchedulePreviewLoaded({
                            assetId,
                            userId,
                            emailHtml: preview?.emailHtml || '',
                            emailSubject: preview?.emailSubject || '',
                            smsBody: preview?.smsBody || '',
                        });
                    }),
                    catchError((err) => {
                        console.error('Failed to fetch schedule preview', err);
                        return of(DispatchActions.sendSchedulePreviewError({ assetId, userId, error: String(err) }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true }
);

/**
 * Submit the Send Schedule action — creates a comment on the Asset to trigger the backend mode handler.
 */

// ─────────────────────────────────────────────────────────────────────────────

/** Persist the event performance requirement to event metadata via bulkEditCalendarEvent */
export const SaveEventPerformanceRequirement = createEffect(
    (
        actions$ = inject(Actions),
        bulkEditCalendarEventGQL = inject(BulkEditCalendarEventGQL),
        notify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.eventPerformanceRequirementChanged),
            exhaustMap(({ eventId, performanceRequirement }) => {
                // metadata is typed as String in EditCalendarEventInput — pass as JSON string.
                // The gateway parses it back to an object before forwarding to Moleculer.
                const input: BulkEditCalendarEventMutationVariables = {
                    edits: [{
                        id: eventId,
                        edit: {
                            metadata: JSON.stringify({ performanceRequirement }),
                        },
                    }],
                };
                return bulkEditCalendarEventGQL.mutate(input, { context: generateMutationContext() }).pipe(
                    map(() => DispatchActions.eventPerformanceRequirementSaveSuccess({ eventId, performanceRequirement })),
                    catchError((err) => {
                        notify.apolloError('Failed to save performance requirement.', err);
                        return of(DispatchActions.eventPerformanceRequirementSaveError({ eventId, error: String(err) }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true }
);

export const SubmitSendSchedule = createEffect(
    (
        actions$ = inject(Actions),
        sendScheduleGQL = inject(SendScheduleToCrewGQL),
        notify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(DispatchActions.sendScheduleSubmitted),
            // concatMap: queue per-route submits in order so all routes are
            // processed even when send() dispatches them in a tight loop.
            concatMap(({ assetId, batchId, date, recipients, customNoteForAsset }) => {
                const recipientCount = recipients.length;
                const summaryText = `Schedule sent to ${recipientCount} crew member${recipientCount !== 1 ? 's' : ''} for ${date}`;

                // Neo4j metadata only supports primitive values (string/number/string[]).
                // Serialize complex objects as JSON strings.
                const serializedMetadata: Record<string, string | number> = {
                    date,
                    assetId,
                    // crewRecipients is an array of objects — must be JSON-stringified
                    crewRecipients: JSON.stringify(recipients),
                    ...(customNoteForAsset ? { customNoteForAsset } : {}),
                };

                return sendScheduleGQL.mutate({
                    input: {
                        objectId: assetId,
                        objectType: 'Asset',
                        mode: 'Send Schedule to Crew',
                        text: summaryText,
                        subject: `Schedule for ${date}`,
                        metadata: serializedMetadata as any,
                        threadMetadata: { date } as any,
                    },
                }).pipe(
                    map((res) => {
                        if (res.data?.addComment?.id) {
                            notify.success(`Schedule sent to ${recipientCount} crew member${recipientCount !== 1 ? 's' : ''}.`);
                            return DispatchActions.sendScheduleSubmitSuccess({
                                assetId,
                                batchId,
                                sentUserIds: recipients.map((r) => r.userId),
                            });
                        }
                        notify.error('Failed to send schedule.');
                        return DispatchActions.sendScheduleSubmitError({ assetId, batchId, error: 'No comment ID returned' });
                    }),
                    catchError((err) => {
                        notify.apolloError('Failed to send schedule.', err);
                        return of(DispatchActions.sendScheduleSubmitError({ assetId, batchId, error: String(err) }));
                    }),
                );
            }),
        );
    },
    { functional: true, dispatch: true }
);
